// F16: ⑤ 依頼文生成 → ユニット別 JSON 生成（LLM 実行）→ 取り込み。
// - 依頼文（request.md）は生成後に人間が編集でき、編集済みの内容がそのまま LLM 実行の文脈になる（要件 5）。
// - JSON はユニット（ファイル）単位で生成し、ajv プリフライト NG は検証エラーを添えて 1 回だけ再生成させる。
// - 生成物は必ず projects/<id>/generated/ に保存し、取り込みは既存 F2（importFromText）を通す。
import { existsSync, mkdirSync, readFileSync, readdirSync, writeFileSync } from 'node:fs';
import path from 'node:path';
import { patchLocaleGameIdentity } from './cast-package.mjs';
import {
    TREASURE_CHEST_COMPASS_ITEM_ID,
    ensureEssentialCharacterInitialItems, ensureEssentialShopAvailability, withEssentialShopEntries,
} from './essential-gameplay-items.mjs';
import { requireGameDir, updateGameMeta } from './games.mjs';
import { summarizeCostLines } from './generation-cost.mjs';
import { importFromText } from './import-json.mjs';
import { deepMergeInto, readJson, readJsonIfExists, walkJson, writeJson } from './json-utils.mjs';
import { callLlm, callLlmJson, mergeUsage, parseJsonLoose } from './llm-client.mjs';
import { auditLog } from './logger.mjs';
import { buildAiRequest } from './prompt-builder.mjs';
import { TOOLS_DIR } from './repo-paths.mjs';
import { collectPurchasableItemIds, forbiddenShopItemReason, sanitizeShopSales } from './shop-policy.mjs';
import { expandBlueprintFills, loadBlueprintDefinition } from './story-blueprints.mjs';
import {
    addUsage,
    projectPaths, readProjectMeta,
    readRequestText,
    readWorldDesign,
    recordStep,
    requireAnalysis,
    updateProjectMeta,
} from './story-project.mjs';
import { asArray, fillTemplate, pipelineConfig, str, toSnakeId } from './story-shared.mjs';
import {
    generationLanguageInstruction, generationLanguageName, localizedForGeneration,
    normalizeStorySubtitle,
} from './story-source.mjs';
import {
    WORLD_DESIGN_FORMAT_VERSION,
    landmarkFootprintRect,
    normalizeWorldDesign,
    projectBlueprintCatalog,
    renderWorldDesignMd,
    repairWorldDesignSurfaceAnchors,
} from './story-world-design.mjs';
import {
    canalIntersectsRect,
    designGroundInfo,
    fillFootprintIntersectsRect,
    hash2D,
    hashSeed,
    isWalkableSurface,
    maxFillTopY, rectsIntersect,
    ringIntersectsRect,
    roadIntersectsRect,
    scatterPointsInShape,
} from './story-world-geometry.mjs';
import { schemaFileFor, validateSingleJson } from './validate-schema.mjs';

const CODE_UNITS = [
    { id: 'game-info', label: '作品情報（game-info.json の novelInfo 差し替え）', target: 'maps/_shared/game-info.json' },
    { id: 'enemy-types', label: '物語敵タイプ登録（ランク能力倍率・生成GLBを反映）', target: 'maps/_shared/enemies.json' },
    { id: 'story-shop', label: '必須・物語固有アイテムショップ（コード整合）', target: 'maps/{startMapId}/shops.json' },
];

// ワールド設計からゲーム実体へ行う決定的補正の世代。配置Y・生成敵スケールなど、
// LLMを再実行せず既存ブラウザ案件へ届ける必要がある変更で更新する。
// v14: 大型NPC（表示高2m以上）へ spawnFootprint を出力（本家Sevenfold 2026-07-16 契約）
// v15: 補完NPCの名前・既定会話の英訳を en.json exact へ不足分だけ登録（validate-i18n 失敗の自己修復）
// v16: 注入する重要地点イベント通知文（eventIdeas）も exact へ不足分登録（対訳未生成は原文のまま）
// v17: LLMが空・部分的な enemies を返した旧案件も、④の全敵配置からプレビュー・⑤保存物を決定的に復元
// v18: 相方プレイアブルの AI チャット NPC（companion 人格BOT）と npc-personas.json をスポーン付近へ決定的生成
// v19: NPC固定会話の既定10件化（companion 既定挨拶10件を既存案件へ反映。⑤-2生成NPCの10件化は再生成時に適用）
// v20: companion の名前ラベル・ゲージを頭上へ（headLabelWorldYAboveOrigin=実身長+マージンのワールド固定方式）
// v21: プレイアブルの HP バーも同方式へ統一（characters.json へ headLabelWorldYAboveOrigin を反映。
//      companion と同じ「そのキャラの実身長基準の頭上」規則。マージン正本は imagePrompts.visualProfile 側）
// v22: プレイヤー専用マージン（playerHeadLabelWorldYMarginMeters）を分離。カメラ近接の perspective で
//      プレイヤー自身のバーだけ高く見えるため、NPC 用より小さい値を characters.json へ併記する
// v23: 通常の生成NPCへ付与していた faceYawOffsetDeg=45 を除去（実機確認で補正方向が逆と判明）
// v24: 通常の生成NPCを正対させる faceYawOffsetDeg=90 を新規生成・既存プレビュー・⑤保存物へ反映
// v26: 銃系武器の使い手の初期弾薬を weaponCatalog.initialAmmoCount へ統一し、ショップで買える弾に限定
//      （既存案件も開いた時点で無償・決定的に揃える。2026-08-01 指示）
export const WORLD_ARTIFACTS_VERSION = 26;
/**
 * 通常生成 NPC の向き補正を移行する境目。WORLD_ARTIFACTS_VERSION とは別に固定する
 * （移行済みの案件を、以後のバージョン上げのたびに作り直さないため）。
 */
export const GENERATED_NPC_YAW_MIGRATION_VERSION = 25;

function availableGameMapIds(gameDir, fallbackMapId) {
    const mapsDir = path.join(gameDir, 'maps');
    if (!existsSync(mapsDir)) return [fallbackMapId].filter(Boolean);
    const mapIds = readdirSync(mapsDir, { withFileTypes: true })
        .filter((entry) => entry.isDirectory() && !entry.name.startsWith('_'))
        .map((entry) => entry.name);
    if (fallbackMapId && !mapIds.includes(fallbackMapId)) mapIds.push(fallbackMapId);
    return mapIds;
}

function castModelsFromPromptIndex(promptIndex) {
    return new Map(asArray(promptIndex?.prompts)
        .filter((entry) => entry?.castId)
        .map((entry) => [entry.castId, {
            role: entry.role,
            humanoid: entry.humanoid !== false,
            hasModel: !!entry.modelGeneratedAt,
            glbPath: entry.glbPath ?? null,
            visualProfile: entry.visualProfile ?? null,
            motionProfile: entry.motionProfile ?? null,
        }]));
}

/** 旧設計（v1・v2.0）も新しい⑤で安全に扱えるよう、読み込み時に現行フォーマットへ移行して保存する。 */
function readNormalizedWorldDesign(projectId, analysis) {
    const current = readWorldDesign(projectId);
    if (!current) return current;
    const migrated = current.formatVersion !== WORLD_DESIGN_FORMAT_VERSION;
    // 現行formatVersionでも、旧処理・手動編集・部分保存でcast配置だけ欠ける場合がある。
    // ⑤で空配置を正として扱わず、解析済みcastとの集合差があれば無償で再正規化する。
    const placementNeedsRepair = [
        ['npc', 'npcPlacements'],
        ['enemy', 'enemyPlacements'],
    ].some(([role, key]) => {
        const expected = new Set(asArray(analysis?.cast).filter((entry) => entry?.role === role).map((entry) => entry.id));
        const placements = asArray(current?.[key]);
        const actual = new Set(placements
            .filter((entry) => role !== 'enemy' || entry?.enabled !== false)
            .map((entry) => entry?.castId)
            .filter((castId) => expected.has(castId)));
        return expected.size !== actual.size
            || [...expected].some((castId) => !actual.has(castId))
            || placements.some((entry) => entry?.castId && !expected.has(entry.castId));
    });
    const normalized = migrated || placementNeedsRepair
        ? normalizeWorldDesign(current, analysis, { blueprints: projectBlueprintCatalog(projectId) })
        : current;
    const repaired = repairWorldDesignSurfaceAnchors(normalized);
    if (!migrated && !placementNeedsRepair && !repaired) return normalized;
    const p = projectPaths(projectId);
    writeJson(p.worldDesign, normalized);
    writeFileSync(p.worldDesignMd, renderWorldDesignMd(normalized), 'utf-8');
    return normalized;
}

// ── ② ベースデータ生成（スキャフォールド）との連携 ───────────────────

/** analysis.json から既存 F1 スキャフォールドの入力値を組み立てる。 */
export function buildScaffoldOptions(projectId, { npmCi = true } = {}) {
    const analysis = requireAnalysis(projectId);
    const g = analysis.game;
    const language = g.generationLanguage === 'en' ? 'en' : 'ja';
    const primaryTitle = localizedForGeneration(g.title, language, g.gameId);
    const primaryDescription = localizedForGeneration(g.description, language, primaryTitle);
    const primarySubtitle = normalizeStorySubtitle(g.subtitle, primaryDescription);
    const primaryName = (character) => localizedForGeneration(character.name, language, character.id);
    return {
        gameId: g.gameId,
        // 入力文書の言語を両ロケールの初期コンテンツへ使い、実行環境で生成基準が変わらないようにする。
        title: { ja: primaryTitle, en: primaryTitle },
        description: { ja: primaryDescription, en: primaryDescription },
        subtitle: { ja: primarySubtitle, en: primarySubtitle },
        originalUrl: g.originalUrl ?? null,
        sourceLanguage: g.sourceLanguage ?? language,
        generationLanguage: language,
        startMapId: g.startMapId,
        startWorldBase: g.startWorldBase, // 開始ワールドの土台（マイワールド 6種・①解析で選定）
        protagonist: g.protagonist,
        characters: {
            boy: { id: g.characters.boy.id, name: { ja: primaryName(g.characters.boy), en: primaryName(g.characters.boy) } },
            girl: { id: g.characters.girl.id, name: { ja: primaryName(g.characters.girl), en: primaryName(g.characters.girl) } },
        },
        npmCi,
    };
}

/** スキャフォールド完了後にプロジェクト⇔ゲームを相互リンクする。 */
export function linkGame(projectId, gameId) {
    updateProjectMeta(projectId, (meta) => ({ ...meta, gameId }));
    updateGameMeta(gameId, (meta) => ({ ...meta, storyProjectId: projectId }));
    recordStep(projectId, 'scaffold', { ok: true, gameId });
}

/**
 * 設定案の編集内容（タイトル・サブタイトル・紹介文・原作URL・ジャンル・世界設定）を、
 * 作成済みの後工程データへ直接反映する（工程データは一切初期化しない）。
 * この機能は軽微な手直し用のため、作り直しが必要になるのは「ファイルの読み込み」
 * 「再解析」のみ（2026-07-15 ユーザー要件）。
 * 反映先: 連携済みゲームの locales(ja/en)・_shared/game-info.json の novelInfo、
 * ⑤-2 生成済み generated/game-info.json（後から再取り込みしても編集前へ戻さないため）。
 * Node 版フルコピー固有の index.html 等は呼び出し側の updateScaffoldIdentity が担う。
 */
export function applyEditedGameSettings(projectId) {
    const analysis = requireAnalysis(projectId);
    const meta = readProjectMeta(projectId);
    const details = [];
    let gameDir = null;
    if (meta.gameId) {
        try { gameDir = requireGameDir(meta.gameId); } catch { gameDir = null; /* ゲーム未実体化なら反映先なし */ }
    }
    if (!gameDir) return { gameId: null, details };
    // 表示値は②と同じ正規化（サブタイトル40字上限等）、タイトル・紹介文の exact 対訳は編集した実値を使う
    const opts = buildScaffoldOptions(projectId, { npmCi: false });
    const identity = { ...opts, title: analysis.game.title, description: analysis.game.description };
    for (const lang of ['ja', 'en']) {
        const localePath = path.join(gameDir, `maps/_shared/i18n/locales/${lang}.json`);
        const locale = readJsonIfExists(localePath);
        if (!locale) continue;
        writeJson(localePath, patchLocaleGameIdentity(locale, identity, lang));
        details.push(`locales/${lang}.json: タイトル・サブタイトル・原作クレジットを更新`);
    }
    const gameInfoPath = path.join(gameDir, 'maps/_shared/game-info.json');
    if (readJsonIfExists(gameInfoPath)) {
        writeJson(gameInfoPath, buildGameInfoJson(gameDir, analysis));
        details.push('game-info.json: novelInfo を更新');
    }
    const generatedGameInfoPath = path.join(projectPaths(projectId).generatedDir, 'game-info.json');
    if (readJsonIfExists(generatedGameInfoPath)) {
        writeJson(generatedGameInfoPath, buildGameInfoJson(gameDir, analysis));
        details.push('generated/game-info.json: novelInfo を更新（再取り込み対策）');
    }
    // ブラウザ版プレビューのキャッシュ版数を進め、更新後のロケール・game-info を確実に配信させる
    updateProjectMeta(projectId, (m) => ({ ...m, previewRev: (m.previewRev ?? 0) + 1 }));
    return { gameId: meta.gameId, details };
}

// ── ⑤-1 依頼文（request.md）─────────────────────────

/**
 * 依頼文を生成して request.md に保存する（既存があれば force 指定時のみ再生成）。
 * 既存 F10（buildAiRequest）の依頼文＋要約シートに、④のワールド設計を添付した形。
 */
export function buildRequestText(projectId, { force = false } = {}) {
    const analysis = requireAnalysis(projectId);
    const meta = readProjectMeta(projectId);
    const p = projectPaths(projectId);
    const existing = readRequestText(projectId);
    if (existing && !force) return { requestText: existing, regenerated: false };
    const { requestText, summarySheet, attachments } = buildAiRequest({
        gameId: meta.gameId ?? analysis.game.gameId,
        summary: analysis.summary,
    });
    const parts = [requestText, '', summarySheet];
    const design = readNormalizedWorldDesign(projectId, analysis);
    if (design) {
        parts.push('', '## 初期ワールド設計（④の生成結果。この設計に従って JSON を作る）', '', renderWorldDesignMd(design));
    }
    const text = parts.join('\n');
    writeFileSync(p.request, text, 'utf-8');
    recordStep(projectId, 'requestText', { chars: text.length, edited: false });
    auditLog('story-request-text', { projectId, chars: text.length, regenerated: true });
    return { requestText: text, regenerated: true, attachments };
}

// ── ⑤-2 ユニット別 JSON 生成 ─────────────────────────

export function listGenerateUnits() {
    return [
        ...pipelineConfig().generate.units.map(({ id, label, target }) => ({ id, label, target, kind: 'llm' })),
        ...CODE_UNITS.map((u) => ({ ...u, kind: 'code' })),
        { id: 'i18n', label: 'i18n パッチ（生文字列の対訳）', target: 'maps/_shared/i18n/locales/{ja,en}.json へマージ', kind: 'code+llm' },
    ];
}

/** 現行パイプラインで必須のLLMユニットのうち、未生成または失敗中のID。 */
export function missingGeneratedLlmUnitIds(generatedStatus) {
    const units = generatedStatus?.units ?? {};
    return pipelineConfig().generate.units
        .filter((unit) => !units[unit.id]?.ok)
        .map((unit) => unit.id);
}

function requireCompleteGeneratedLlmUnits(projectId) {
    const status = readJsonIfExists(projectPaths(projectId).generatedStatus);
    const missing = missingGeneratedLlmUnitIds(status);
    if (missing.length > 0) {
        throw new Error(`未生成のJSONがあります: ${missing.join(', ')}。先に「⑥-2 JSON生成」で未生成ユニットを生成してください。`);
    }
    const p = projectPaths(projectId);
    const missingFiles = pipelineConfig().generate.units
        .filter((unit) => !existsSync(path.join(p.generatedDir, `${unit.id}.json`)))
        .map((unit) => unit.id);
    if (missingFiles.length > 0) {
        throw new Error(`生成済み記録に対応するJSONファイルがありません: ${missingFiles.join(', ')}。⑥-2 JSON生成を再実行して復元してください。`);
    }
    return status;
}

/** ゲームの _shared マスタから、生成時に参照させる既存 ID カタログを作る。 */
function buildCatalogs(gameDir) {
    const enemies = readJsonIfExists(path.join(gameDir, 'maps/_shared/enemies.json'));
    const loot = readJsonIfExists(path.join(gameDir, 'maps/_shared/loot-tables.json'));
    return {
        enemyTypes: asArray(enemies?.enemies).map((e) => ({ id: e.id, name: e.name })),
        lootTables: asArray(loot?.lootTables).map((t) => t.id).filter(Boolean),
    };
}

export function buildUnitContext({ unit, analysis, design, catalogs, generatedMeta, visualProfiles = new Map(), motionProfiles = new Map() }) {
    const language = analysis.game?.generationLanguage === 'en' ? 'en' : 'ja';
    const parts = [
        '## 生成言語（最優先）',
        generationLanguageInstruction(language),
        '',
    ];
    for (const part of unit.contextParts ?? []) {
        if (part === 'worldDesign' && design) {
            parts.push('## 初期ワールド設計（この設計に従う）', JSON.stringify(design, null, 1), '');
        } else if (part === 'worldTemplates') {
            const options = pipelineConfig().startWorldBases?.options ?? [];
            parts.push('## ベースワールド仕様（空・地面・天候の整合に使う）', JSON.stringify(options.map((entry) => ({
                id: entry.id,
                label: entry.label,
                hint: entry.hint,
                sky: entry.sky,
                defaultWeather: entry.defaultWeather,
                forcedWeather: entry.forcedWeather,
                strengths: entry.strengths,
                limitations: entry.limitations,
            })), null, 1), '');
        } else if (part === 'castNpcs') {
            // protagonist / partner はプレイヤー自身（プレイアブル 2 キャラ）のため NPC 配置対象から除外する
            const npcs = analysis.cast
                .filter((c) => c.role === 'npc')
                .map((c) => ({
                    id: c.id, name: localizedForGeneration(c.name, language, c.id), humanoid: c.humanoid, personality: c.personality,
                    locationHint: c.locationHint, visualProfile: visualProfiles.get(c.id) ?? null,
                }));
            parts.push('## 配置候補キャスト（id をそのまま NPC の id に使う。この一覧に無い人物は配置しない）', JSON.stringify(npcs, null, 1), '');
            if (design) parts.push('## NPC 配置設計', JSON.stringify(design.npcPlacements, null, 1), '');
        } else if (part === 'enemyCatalog') {
            parts.push('## 利用可能な敵タイプ一覧（type はこの id のみ使用可）', JSON.stringify(catalogs.enemyTypes), '');
            parts.push('## 利用可能なルートテーブル一覧（loot はこの id のみ使用可）', JSON.stringify(catalogs.lootTables), '');
            const storyEnemies = analysis.cast.filter((c) => c.role === 'enemy').map((c) => ({
                id: c.id, name: localizedForGeneration(c.name, language, c.id), appearance: c.appearance, humanoid: c.humanoid, enemyRank: c.enemyRank,
                visualProfile: visualProfiles.get(c.id) ?? null,
                motionProfile: motionProfiles.get(c.id) ?? null,
            }));
            parts.push('## 物語の敵（見た目・名前の近い既存タイプへ割り当てる）', JSON.stringify(storyEnemies, null, 1), '');
            if (design) parts.push('## 敵配置設計', JSON.stringify(design.enemyPlacements, null, 1), '');
        } else if (part === 'metaAreas') {
            const areas = asArray(generatedMeta?.areas).map((a) => ({ id: a.id, name: a.name }));
            const fallback = design ? design.areas.map((a) => ({
                id: a.id,
                name: language === 'en' ? (a.nameEn || a.nameJa) : (a.nameJa || a.nameEn),
            })) : [];
            parts.push('## 使用可能な areaId（meta.json に定義済みのもののみ）', JSON.stringify(areas.length ? areas : fallback), '');
        } else if (part === 'eventList') {
            parts.push('## 章イベント（作品要約シートより）', analysis.summary.events || '（情報なし）', '');
            if (design?.eventIdeas?.length) parts.push('## イベント案（ワールド設計より）', JSON.stringify(design.eventIdeas, null, 1), '');
        } else if (part === 'storyContent') {
            const cfg = pipelineConfig().storyContentGeneration ?? {};
            const playable = analysis.cast
                .filter((cast) => cast.role === 'protagonist' || cast.role === 'partner')
                .map((cast) => ({
                    id: cast.id,
                    role: cast.role,
                    name: cast.name,
                    appearance: cast.appearance,
                    personality: cast.personality,
                }));
            parts.push('## 世界観のコンテンツ制約（最優先）', JSON.stringify(analysis.contentRules ?? { magic: 'uncertain' }, null, 1), '');
            parts.push('## 物語から抽出・企画したアイテム/スキル', JSON.stringify({
                items: analysis.summary.items,
                skills: analysis.summary.skills,
                worldSetting: analysis.game.worldSetting,
                genre: analysis.game.genre,
            }, null, 1), '');
            parts.push('## プレイアブルキャラ（characterSkills のキーはこのIDだけ）', JSON.stringify(playable, null, 1), '');
            // 物語固有アイテムは「使うと必ず良いことが起きる」効果だけを選ばせる（2026-07-26 指示）。
            // material（効果なし）はカタログごと見せない＝素材アイテムを作らせない
            const itemPool = storyItemEffectPool();
            const itemCatalog = Object.fromEntries(itemPool.map((key) => [key, (cfg.itemEffects ?? {})[key]]).filter(([, v]) => v));
            parts.push('## 利用可能なアイテム効果（このカタログ外は実装不可）', JSON.stringify(itemCatalog, null, 1), '');
            parts.push('- アイテムは必ずこのカタログの効果を 1 つ持たせる（効果なしの素材アイテムは作らない）');
            const requiredEffects = asArray(cfg.storyItemPolicy?.requiredEffects);
            if (requiredEffects.length > 0) {
                parts.push(`- 次の効果は必ず 1 つ以上のアイテムへ割り当てる（無いと序盤が詰む）: ${requiredEffects.join(' / ')}`);
            }
            parts.push('');
            parts.push('## 利用可能なアクティブスキル効果（このカタログ外は実装不可）', JSON.stringify(cfg.activeSkillEffects ?? {}, null, 1), '');
            // レベル補正はエンジン側が effect ごとに必ず適用する。説明文にも成長を書かせる（2026-07-26 指示）
            parts.push('- スキルはレベルが上がるほど強くなる（下記の levelScaling）。description にもその成長を1文で書く');
            parts.push('');
            // 攻撃アイテム・武器カタログ（docs/攻撃方法の実装案.md §6）。原文に銃器・爆発物が
            // 登場する作品でのみ使わせる。カタログ外の delivery・効果・エフェクトIDは正規化で棄却される
            const weaponItemCatalog = Object.fromEntries(
                [...WEAPON_ITEM_EFFECTS]
                    .map((key) => [key, (cfg.itemEffects ?? {})[key]])
                    .filter(([, value]) => value));
            const weaponCatalog = cfg.weaponCatalog ?? {};
            if (Object.keys(weaponItemCatalog).length > 0 && weaponCatalog.deliveries) {
                parts.push('## 攻撃アイテム効果と武器テンプレ（原文に銃器・爆発物・設置兵器が登場する場合のみ使用可。このカタログ外は実装不可）',
                    JSON.stringify({ itemEffects: weaponItemCatalog, weaponCatalog }, null, 1), '');
                parts.push('- weapons[] の武器と弾薬（effect=fire_equipped_weapon の items）は必ずペアで設計し、ammoClass を一致させる');
                parts.push('- 銃火器（delivery=burst）・感知トラップ・タレットは contentRules.technology が modern/futuristic/mixed のときだけ設計する');
                // 初期弾薬の個数は weaponCatalog.initialAmmoCount 1 か所だけが正。ここでも同じ値を埋め込み、
                // 設計（この出力）と取り込み（ensureStoryWeaponLoadout）で食い違わないようにする
                parts.push(`- 弾薬アイテムは必ずアイテムショップで買える設定にする（shopForbidden を付けない）。武器の使い手は開始時にその弾薬を ${storyInitialAmmoCount()} 個持って始まるため、買い足せない弾は撃ち切ると武器が死に装備になる`);
                parts.push('');
            }
        }
    }
    return parts.join('\n');
}

const cloneJson = (value) => (value == null ? value : JSON.parse(JSON.stringify(value)));
const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;
const int = (value, fallback = 0) => Number.isFinite(Number(value)) ? Math.floor(Number(value)) : fallback;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const STORY_ITEM_ICONS = new Set([
    'potion_red', 'potion_gold', 'herb', 'herb_green', 'bomb_flash', 'bomb_smoke',
    'bottle_green', 'bottle_blue', 'bottle_orange', 'bottle_purple', 'scroll_blue',
    'crystal', 'gear', 'feather', 'fang', 'fang_large', 'orb', 'star', 'charm_shield',
]);

function isMagicStoryEntryBlocked(entry, analysis) {
    if (analysis?.contentRules?.magic !== 'absent') return false;
    if (entry?.storyTheme === 'magic') return true;
    const keywords = asArray(pipelineConfig().storyContentGeneration?.magicKeywords)
        .map((value) => String(value).toLowerCase())
        .filter(Boolean);
    const text = [entry?.name, entry?.description, entry?.sourceBasis]
        .filter((value) => typeof value === 'string')
        .join(' ')
        .toLowerCase();
    return keywords.some((keyword) => text.includes(keyword));
}

function storyThemeOf(value) {
    const allowed = asArray(pipelineConfig().storyContentGeneration?.allowedThemes);
    return allowed.includes(value) ? value : 'general';
}

function uniqueStoryId(rawId, fallback, usedIds) {
    const core = toSnakeId(rawId, fallback).replace(/^story_+/, '') || fallback;
    let id = `story_${core}`;
    let suffix = 2;
    while (usedIds.has(id)) id = `story_${core}_${suffix++}`;
    usedIds.add(id);
    return id;
}

/**
 * 物語固有アイテムへ割り当ててよい効果（2026-07-26 指示）。
 * 「使っても何も起きないアイテム」を作らないため material（効果なし）は必ず除外する。
 * 設定が壊れていてもカタログから material を落とした一覧へフォールバックする。
 */
function storyItemEffectPool() {
    const cfg = pipelineConfig().storyContentGeneration ?? {};
    const catalog = cfg.itemEffects ?? {};
    const configured = asArray(cfg.storyItemPolicy?.pool)
        .map((key) => str(key))
        .filter((key) => key && key !== 'material' && catalog[key]);
    if (configured.length > 0) return configured;
    return Object.keys(catalog).filter((key) => key !== 'material' && catalog[key]?.type !== 'material');
}

/**
 * 武器・攻撃アイテム系の効果キー（docs/攻撃方法の実装案.md §6.5）。
 * storyItemPolicy.pool（未指定時のローテーション割当）とは別枠で、LLM が明示指定した
 * 場合にのみ許可する。追加フィールドはカタログ値域へ丸め、実行コードは受け付けない。
 */
const WEAPON_ITEM_EFFECTS = new Set(['fire_equipped_weapon', 'throw_explosive', 'throw_debuff', 'place_device']);

/**
 * 初期弾薬数の既定（config の weaponCatalog.initialAmmoCount が欠けた場合のみ使用）。
 * 正本は config/story-pipeline.json 側なので、値を変えるときは両方を同時に変える。
 */
const STORY_INITIAL_AMMO_FALLBACK = 10;

function weaponCatalogConfig() {
    return pipelineConfig().storyContentGeneration?.weaponCatalog ?? {};
}

/**
 * contentRules.technology が weaponCatalog.technologyAllow の許可リストへ含まれるか。
 * リスト未定義の種別は常に許可（弩・投擲は魔法世界でも成立）。uncertain は不許可（fail closed）。
 */
function technologyAllowsWeaponKind(kind, analysis) {
    const list = asArray(weaponCatalogConfig().technologyAllow?.[kind]).map((v) => str(v)).filter(Boolean);
    if (list.length === 0) return true;
    return list.includes(str(analysis?.contentRules?.technology));
}

/** カタログの {min,max,default} レンジへ丸める（非数・範囲外は default → clamp）。 */
function clampCatalogRange(rangeCfg, rawValue) {
    const min = finite(rangeCfg?.min, 0);
    const max = finite(rangeCfg?.max, min);
    const def = clamp(finite(rangeCfg?.default, min), min, max);
    const value = Number.isFinite(Number(rawValue)) ? Number(rawValue) : def;
    return clamp(value, min, max);
}

/**
 * 武器・攻撃アイテム効果の追加フィールドをカタログ値域へ丸めて付与する。
 * エフェクト ID・実行コードは受け付けない（エンジンが effect 種別から決定する）。
 */
function applyWeaponItemFields(item, raw, spec) {
    const pickIcon = (fallbackIcon) => (STORY_ITEM_ICONS.has(raw?.icon) ? raw.icon : fallbackIcon);
    if (item.effect === 'fire_equipped_weapon') {
        item.ammoClass = toSnakeId(raw?.ammoClass, 'ammo');
        item.icon = pickIcon('gear');
    } else if (item.effect === 'throw_explosive') {
        item.throwRangeBlocks = Math.round(clampCatalogRange(spec.throwRangeBlocks, raw?.throwRangeBlocks));
        item.explosionRadius = Math.round(clampCatalogRange(spec.explosionRadius, raw?.explosionRadius));
        item.explosionBlockBreakSize = Math.round(clampCatalogRange(spec.explosionBlockBreakSize, raw?.explosionBlockBreakSize));
        item.icon = pickIcon('bomb_flash');
    } else if (item.effect === 'throw_debuff') {
        const allowed = asArray(spec.debuffEffects).map((v) => str(v)).filter(Boolean);
        item.debuffEffect = allowed.includes(str(raw?.debuffEffect)) ? str(raw.debuffEffect) : (allowed[0] ?? 'slow');
        item.debuffDurationSec = Math.round(clampCatalogRange(spec.debuffDurationSec, raw?.debuffDurationSec));
        item.throwRangeBlocks = Math.round(clampCatalogRange(spec.throwRangeBlocks, raw?.throwRangeBlocks));
        item.explosionRadius = Math.round(clampCatalogRange(spec.explosionRadius, raw?.explosionRadius));
        item.icon = pickIcon('bomb_smoke');
    } else if (item.effect === 'place_device') {
        const kinds = asArray(spec.deviceTypes).map((v) => str(v)).filter(Boolean);
        item.deviceType = kinds.includes(str(raw?.deviceType)) ? str(raw.deviceType) : 'time_bomb';
        item.explosionRadius = Math.round(clampCatalogRange(spec.explosionRadius, raw?.explosionRadius));
        item.explosionBlockBreakSize = Math.round(clampCatalogRange(spec.explosionBlockBreakSize, raw?.explosionBlockBreakSize));
        item.durationSec = Math.round(clampCatalogRange(spec.durationSec, raw?.durationSec));
        item.maxActiveDevices = Math.round(clampCatalogRange(spec.maxActiveDevices, raw?.maxActiveDevices));
        if (item.deviceType === 'time_bomb') {
            item.fuseSec = Math.round(clampCatalogRange(spec.fuseSec, raw?.fuseSec));
        } else if (item.deviceType === 'proximity_trap') {
            item.armDelaySec = Math.round(clampCatalogRange(spec.armDelaySec, raw?.armDelaySec));
            item.triggerRadius = Math.round(clampCatalogRange(spec.triggerRadius, raw?.triggerRadius));
        } else if (item.deviceType === 'turret') {
            item.intervalMs = Math.round(clampCatalogRange(spec.intervalMs, raw?.intervalMs));
            item.rangeBlocks = Math.round(clampCatalogRange(spec.rangeBlocks, raw?.rangeBlocks));
        }
        item.icon = pickIcon('gear');
    }
}

/**
 * 物語武器（weapons[]）を weaponCatalog へ正規化し、弾薬との対を保証する（§6.5）。
 * - delivery は weaponCatalog.deliveries のキーのみ。burst は technology ゲート対象（違反は棄却）
 * - castEffectId / hitEffectId は LLM 出力を無視し、カタログから決定的に付与する
 * - 弾薬だけ・武器だけの片割れは、カタログ既定値で相方を自動補完する（MP回復の保証と同じ思想）
 * - 正規化した武器は items.json の weapons[] に載せ、取り込み時に equipment.json へマージする
 */
function normalizeStoryWeapons(input, base, analysis, usedIds, language) {
    const catalogW = weaponCatalogConfig();
    const deliveries = catalogW.deliveries ?? {};
    const preservedWeapons = asArray(base.weapons).filter((w) => w?.storyGenerated !== true);
    if (Object.keys(deliveries).length === 0) {
        if (preservedWeapons.length > 0) base.weapons = preservedWeapons;
        else delete base.weapons;
        return;
    }
    const maxWeapons = Math.max(1, int(catalogW.maxWeapons, 2));
    const playableIds = [analysis?.game?.characters?.boy?.id, analysis?.game?.characters?.girl?.id].filter(Boolean);
    const playableAliases = new Map();
    for (const cast of asArray(analysis?.cast)) {
        const playableId = cast?.role === 'protagonist'
            ? analysis?.game?.characters?.[analysis?.game?.protagonist]?.id
            : cast?.role === 'partner'
                ? analysis?.game?.characters?.[analysis?.game?.protagonist === 'boy' ? 'girl' : 'boy']?.id
                : null;
        if (cast?.id && playableId) playableAliases.set(cast.id, playableId);
    }
    const protagonistId = analysis?.game?.characters?.[analysis?.game?.protagonist]?.id ?? playableIds[0] ?? '';
    const resolveOwner = (rawOwner) => {
        const owner = str(rawOwner);
        if (playableIds.includes(owner)) return owner;
        return playableAliases.get(owner) ?? protagonistId;
    };
    const buildWeapon = (raw, index) => {
        const rawSpec = raw?.weaponSpec && typeof raw.weaponSpec === 'object' ? raw.weaponSpec : {};
        const delivery = str(rawSpec.delivery) || 'projectile';
        if (!deliveries[delivery]) return null;
        if (delivery === 'burst' && !technologyAllowsWeaponKind('burstWeapons', analysis)) return null;
        const theme = storyThemeOf(raw?.storyTheme);
        if (isMagicStoryEntryBlocked({ ...raw, storyTheme: theme }, analysis)) return null;
        const tmpl = deliveries[delivery];
        const id = uniqueStoryId(raw?.id ?? raw?.name, `weapon_${index + 1}`, usedIds);
        const name = str(raw?.name) || (language === 'en' ? `Story weapon ${index + 1}` : `物語の武器${index + 1}`);
        const explosionRadius = Math.round(clampCatalogRange(tmpl.explosionRadius, rawSpec.explosionRadius));
        const weaponSpec = {
            delivery,
            ammoClass: toSnakeId(rawSpec.ammoClass ?? `${id}_ammo`, `ammo_${index + 1}`),
            intervalMs: Math.round(clampCatalogRange(tmpl.intervalMs, rawSpec.intervalMs)),
            damagePerHit: Math.round(clampCatalogRange(tmpl.damagePerHit, rawSpec.damagePerHit)),
            rangeBlocks: Math.round(clampCatalogRange(tmpl.rangeBlocks, rawSpec.rangeBlocks)),
            projectileSpeed: Math.round(clampCatalogRange(tmpl.projectileSpeed, rawSpec.projectileSpeed)),
            explosionRadius,
            explosionBlockBreakSize: Math.round(clampCatalogRange(tmpl.explosionBlockBreakSize, rawSpec.explosionBlockBreakSize)),
            bulletBlockBreak: rawSpec.bulletBlockBreak === false ? false : tmpl.bulletBlockBreakDefault !== false,
            castEffectId: str(catalogW.castEffectIds?.[delivery]) || 'gunshot_cast',
            hitEffectId: explosionRadius > 0
                ? (str(catalogW.hitEffectIds?.explosion) || 'explosion_blast')
                : (str(catalogW.hitEffectIds?.direct) || 'gunshot_hit'),
        };
        for (const key of ['homingTurnRate', 'maxBounces', 'pierceCount', 'knockbackBlocks']) {
            const value = Math.round(clampCatalogRange(catalogW.optionRanges?.[key], rawSpec[key] ?? 0));
            if (value > 0) weaponSpec[key] = value;
        }
        if (rawSpec.penetratesBlocks === true) weaponSpec.penetratesBlocks = true;
        const shopPrice = clamp(Math.floor(Number(raw?.shopPrice) || 5000), 100, 100000);
        return {
            id,
            slot: 'weapon',
            name,
            nameKey: `equipment.${id}.name`,
            // 装備一覧・ショップ表示用の武器 SVG アイコン（エンジン ICON_BUILDERS に実在する ID のみ。
            // 未指定だと「？」フォールバックになるため必ず付与する。2026-08-01 指示）
            iconId: str(catalogW.iconIds?.[delivery]) || (delivery === 'burst' ? 'weapon_gun' : 'weapon_launcher'),
            sellPrice: clamp(Math.floor(Number(raw?.sellPrice) || Math.max(1, shopPrice / 10)), 0, shopPrice - 1),
            shopPrice,
            description: str(raw?.description) || `${name}${language === 'en' ? '.' : '。'}`,
            descriptionKey: `equipment.${id}.description`,
            owner: resolveOwner(raw?.owner ?? raw?.user),
            storyGenerated: true,
            storyTheme: theme,
            sourceBasis: str(raw?.sourceBasis),
            weaponSpec,
        };
    };
    const weapons = [];
    for (const [index, raw] of asArray(input?.weapons).entries()) {
        if (weapons.length >= maxWeapons) break;
        const built = buildWeapon(raw, index);
        if (built) weapons.push(built);
    }
    // ペア保証①: 弾薬だけがある場合、対応武器をカタログ既定値で自動補完する
    const ammoItems = base.items.filter((item) => item?.storyGenerated === true && item?.effect === 'fire_equipped_weapon');
    for (const ammo of ammoItems) {
        if (weapons.some((weapon) => weapon.weaponSpec.ammoClass === ammo.ammoClass)) continue;
        if (weapons.length >= maxWeapons) break;
        const delivery = (Number(ammo.effectValue) > 1 && technologyAllowsWeaponKind('burstWeapons', analysis))
            ? 'burst'
            : 'projectile';
        const built = buildWeapon({
            id: `${ammo.ammoClass}_launcher`,
            name: language === 'en' ? `${ammo.name} Launcher` : `${ammo.name}の発射器`,
            description: language === 'en'
                ? `A weapon that fires ${ammo.name}. Equip it to use the ammo.`
                : `${ammo.name}を発射する武器。装備すると弾薬を使えるようになる。`,
            storyTheme: ammo.storyTheme,
            sourceBasis: str(ammo.sourceBasis),
            weaponSpec: { delivery, ammoClass: ammo.ammoClass },
        }, weapons.length);
        if (built) weapons.push(built);
    }
    // ペア保証②: 武器だけがある場合、弾薬アイテムを自動補完する（maxItems の上限外で追加）
    const ammoSpec = (pipelineConfig().storyContentGeneration?.itemEffects ?? {}).fire_equipped_weapon ?? {};
    for (const weapon of weapons) {
        const ammoClass = weapon.weaponSpec.ammoClass;
        if (base.items.some((item) => item?.storyGenerated === true
            && item?.effect === 'fire_equipped_weapon' && item?.ammoClass === ammoClass)) continue;
        const id = uniqueStoryId(`${ammoClass}_ammo`, `ammo_${base.items.length + 1}`, usedIds);
        const rounds = Math.round(clampCatalogRange(ammoSpec.effectValue, weapon.weaponSpec.delivery === 'burst' ? 30 : 1));
        const name = language === 'en' ? `${weapon.name} Ammo` : `${weapon.name}の弾薬`;
        base.items.push({
            id,
            name,
            nameKey: `items.${id}.name`,
            type: 'consumable',
            icon: 'gear',
            effect: 'fire_equipped_weapon',
            effectValue: rounds,
            ammoClass,
            sortGroup: 3,
            sellPrice: 100,
            shopPrice: 1000,
            shopExclusive: true,
            storyGenerated: true,
            storyTheme: weapon.storyTheme,
            sourceBasis: '',
            description: language === 'en'
                ? `Ammunition for ${weapon.name} (${rounds} rounds per use).`
                : `${weapon.name}用の弾薬（1回で${rounds}発）。`,
            descriptionKey: `items.${id}.description`,
            alwaysConsume: true,
        });
    }
    const nextWeapons = [...preservedWeapons, ...weapons];
    if (nextWeapons.length > 0) base.weapons = nextWeapons;
    else delete base.weapons;
}

function normalizeStoryItems(input, existingTarget, analysis) {
    const cfg = pipelineConfig().storyContentGeneration ?? {};
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    const catalog = cfg.itemEffects ?? {};
    const base = cloneJson(existingTarget ?? { formatVersion: '1.0', items: [] });
    base.items = asArray(base.items).filter((item) => item?.storyGenerated !== true);
    const usedIds = new Set(base.items.map((item) => item?.id).filter(Boolean));
    const effectPool = storyItemEffectPool();
    let index = 0;
    for (const raw of asArray(input?.items).slice(0, Number(cfg.maxItems) || 8)) {
        const requested = raw?.effect == null ? '' : String(raw.effect).trim();
        // 効果なし（未指定・material 等）は捨てずに有益な効果へ決定的に割り当てる
        //＝「使っても何も起きないアイテム」を作らない（2026-07-26 指示）。
        // ただしカタログ外の効果（未実装・課金ペット等の禁止効果）は従来どおり捨てる。
        // ここを一緒に救い上げると、販売禁止品が普通のアイテムとして復活してしまう
        let rawEffect;
        if (WEAPON_ITEM_EFFECTS.has(requested) && catalog[requested]) {
            // 武器・攻撃アイテム系（§6.5）: 明示指定時のみ許可し、technology ゲートで
            // 世界観に合わない設置兵器を fail closed で棄却する（地雷・タレットは modern 以上）
            if (requested === 'place_device') {
                const deviceKind = str(raw?.deviceType);
                if (deviceKind === 'proximity_trap' && !technologyAllowsWeaponKind('proximityTrap', analysis)) continue;
                if (deviceKind === 'turret' && !technologyAllowsWeaponKind('turret', analysis)) continue;
            }
            rawEffect = requested;
        } else if (effectPool.includes(requested)) rawEffect = requested;
        else if (requested === '' || catalog[requested]) rawEffect = effectPool[index % effectPool.length] ?? '';
        else continue;
        const spec = catalog[rawEffect];
        if (!spec) continue;
        const theme = storyThemeOf(raw?.storyTheme);
        const candidate = { ...raw, storyTheme: theme };
        if (isMagicStoryEntryBlocked(candidate, analysis)) continue;
        const id = uniqueStoryId(raw?.id ?? raw?.name, `item_${++index}`, usedIds);
        const name = str(raw?.name) || (language === 'en' ? `Story item ${index}` : `物語の道具${index}`);
        const description = str(raw?.description) || str(raw?.sourceBasis) || `${name}${language === 'en' ? '.' : '。'}`;
        const valueCfg = spec.effectValue ?? { min: 0, max: 0, default: 0 };
        const effectValue = clamp(
            Number.isFinite(Number(raw?.effectValue)) ? Number(raw.effectValue) : Number(valueCfg.default) || 0,
            Number(valueCfg.min) || 0,
            Number(valueCfg.max) || 0,
        );
        const shopPrice = clamp(Math.floor(Number(raw?.shopPrice) || 1000), 100, 100000);
        const sellPrice = clamp(Math.floor(Number(raw?.sellPrice) || Math.max(1, shopPrice / 10)), 0, shopPrice - 1);
        const icon = STORY_ITEM_ICONS.has(raw?.icon) ? raw.icon : (spec.type === 'material' ? 'crystal' : 'potion_red');
        const item = {
            id,
            name,
            nameKey: `items.${id}.name`,
            type: spec.type === 'material' ? 'material' : 'consumable',
            icon,
            effect: spec.type === 'material' ? null : rawEffect,
            effectValue,
            sortGroup: spec.type === 'material' ? 8 : (rawEffect.startsWith('heal_') || rawEffect === 'cure_poison' ? 2 : 3),
            sellPrice,
            shopPrice,
            shopExclusive: true,
            storyGenerated: true,
            storyTheme: theme,
            sourceBasis: str(raw?.sourceBasis),
            description,
            descriptionKey: `items.${id}.description`,
            ...(spec.type === 'consumable' ? { alwaysConsume: true } : {}),
        };
        if (rawEffect === 'damage_enemy_fixed') {
            const elements = asArray(spec.allowedDamageElements);
            item.damageElement = elements.includes(raw?.damageElement) ? raw.damageElement : 'physical';
        }
        if (WEAPON_ITEM_EFFECTS.has(rawEffect)) {
            applyWeaponItemFields(item, raw, spec);
        }
        base.items.push(item);
    }
    // MP 回復手段が 1 つも無いと序盤のゲームプレイが成立しないため、生成物に無ければ定型で足す
    //（2026-07-26 指示。maxItems は作品性のための目安なので、この保証分は上限外で追加する）
    for (const requiredEffect of asArray(cfg.storyItemPolicy?.requiredEffects).map((v) => str(v)).filter(Boolean)) {
        if (!catalog[requiredEffect]) continue;
        if (base.items.some((item) => item?.storyGenerated === true && item?.effect === requiredEffect)) continue;
        const fallback = cfg.storyItemPolicy?.fallbackNames?.[requiredEffect]?.[language] ?? {};
        const id = uniqueStoryId(`${requiredEffect}_fallback`, `item_${++index}`, usedIds);
        const name = str(fallback.name) || (language === 'en' ? 'Mana Drop' : '魔力の雫');
        const valueCfg = catalog[requiredEffect].effectValue ?? { default: 0, min: 0, max: 0 };
        base.items.push({
            id,
            name,
            nameKey: `items.${id}.name`,
            type: 'consumable',
            icon: 'bottle_blue',
            effect: requiredEffect,
            effectValue: clamp(Number(valueCfg.default) || 0, Number(valueCfg.min) || 0, Number(valueCfg.max) || 0),
            sortGroup: 2,
            sellPrice: 30,
            shopPrice: 300,
            shopExclusive: true,
            storyGenerated: true,
            storyTheme: 'general',
            sourceBasis: '',
            description: str(fallback.description) || (language === 'en'
                ? 'A basic recovery item that restores MP.'
                : '魔力を回復する基本の回復薬。'),
            descriptionKey: `items.${id}.description`,
            alwaysConsume: true,
        });
    }
    // 物語武器の正規化と弾薬ペア保証（§6.5）。weapons[] は items.json を運搬役にし、
    // ⑤-3 取り込み時に equipment.json（weapon スロット）と初期装備へ反映する
    normalizeStoryWeapons(input, base, analysis, usedIds, language);
    base.formatVersion = typeof base.formatVersion === 'string' ? base.formatVersion : '1.0';
    return base;
}

function normalizeStorySkills(input, existingTarget, analysis) {
    const cfg = pipelineConfig().storyContentGeneration ?? {};
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    const catalog = cfg.activeSkillEffects ?? {};
    const base = cloneJson(existingTarget ?? { formatVersion: '1.1', passiveSkills: [], activeSkills: [], enemySkills: [], characterSkills: {} });
    const previousGeneratedIds = new Set([
        ...asArray(base.passiveSkills),
        ...asArray(base.activeSkills),
        ...asArray(base.enemySkills),
    ].filter((skill) => skill?.storyGenerated === true).map((skill) => skill.id));
    base.passiveSkills = asArray(base.passiveSkills).filter((skill) => skill?.storyGenerated !== true);
    base.activeSkills = asArray(base.activeSkills).filter((skill) => skill?.storyGenerated !== true);
    base.enemySkills = asArray(base.enemySkills).filter((skill) => skill?.storyGenerated !== true);
    const usedIds = new Set([
        ...base.passiveSkills,
        ...base.activeSkills,
        ...base.enemySkills,
    ].map((skill) => skill?.id).filter(Boolean));
    const idAliases = new Map();
    const generated = [];
    let index = 0;
    for (const raw of asArray(input?.activeSkills).slice(0, Number(cfg.maxActiveSkills) || 6)) {
        const effect = typeof raw?.effect === 'string' ? raw.effect.trim() : '';
        const template = catalog[effect];
        if (!template) continue;
        const theme = storyThemeOf(raw?.storyTheme);
        const candidate = { ...raw, storyTheme: theme };
        if (isMagicStoryEntryBlocked(candidate, analysis)) continue;
        const rawId = toSnakeId(raw?.id ?? raw?.name, `skill_${++index}`);
        const id = uniqueStoryId(rawId, `skill_${index}`, usedIds);
        idAliases.set(rawId, id);
        const name = str(raw?.name) || (language === 'en' ? `Story skill ${index}` : `物語の技${index}`);
        // levelScaling は生成プロンプト向けの説明（レベル補正はエンジンが effect ごとに適用する）。
        // ゲームデータへは持ち込まない
        const { levelScaling: _levelScaling, ...runtimeTemplate } = cloneJson(template);
        generated.push({
            ...runtimeTemplate,
            id,
            name,
            effect,
            storyGenerated: true,
            storyTheme: theme,
            sourceBasis: str(raw?.sourceBasis),
            description: str(raw?.description) || `${name}${language === 'en' ? '.' : '。'}`,
        });
    }
    base.activeSkills.push(...generated);

    const playableIds = [analysis?.game?.characters?.boy?.id, analysis?.game?.characters?.girl?.id].filter(Boolean);
    const playableAliases = new Map();
    for (const cast of asArray(analysis?.cast)) {
        const playableId = cast?.role === 'protagonist'
            ? analysis?.game?.characters?.[analysis?.game?.protagonist]?.id
            : cast?.role === 'partner'
                ? analysis?.game?.characters?.[analysis?.game?.protagonist === 'boy' ? 'girl' : 'boy']?.id
                : null;
        if (cast?.id && playableId) playableAliases.set(cast.id, playableId);
    }
    const rawAssignments = input?.characterSkills && typeof input.characterSkills === 'object' ? input.characterSkills : {};
    const assignmentsByPlayable = new Map(playableIds.map((id) => [id, []]));
    for (const [rawCharacterId, skillIds] of Object.entries(rawAssignments)) {
        const playableId = playableAliases.get(rawCharacterId) ?? rawCharacterId;
        if (assignmentsByPlayable.has(playableId)) assignmentsByPlayable.get(playableId).push(...asArray(skillIds));
    }
    const generatedIds = new Set(generated.map((skill) => skill.id));
    const maxPerPlayable = Number(cfg.maxSkillsPerPlayable) || 3;
    const previousAssignments = base.characterSkills && typeof base.characterSkills === 'object'
        ? cloneJson(base.characterSkills)
        : {};
    base.characterSkills = { ...previousAssignments };
    for (const [playableIndex, characterId] of playableIds.entries()) {
        const assigned = asArray(assignmentsByPlayable.get(characterId))
            .map((rawId) => idAliases.get(toSnakeId(rawId, '')) ?? rawId)
            .filter((id) => generatedIds.has(id))
            .slice(0, maxPerPlayable);
        if (assigned.length === 0 && generated.length > 0) {
            assigned.push(generated[playableIndex % generated.length].id);
        }
        const preserved = asArray(previousAssignments[characterId])
            .filter((id) => !previousGeneratedIds.has(id));
        base.characterSkills[characterId] = [...new Set([...preserved, ...assigned])];
    }
    base.formatVersion = typeof base.formatVersion === 'string' ? base.formatVersion : '1.1';
    return base;
}

export function buildStoryShopJson({ items, startMapId }) {
    const storyItems = asArray(items?.items).filter((item) => item?.storyGenerated === true
        && !forbiddenShopItemReason(item, [startMapId]));
    // 物語武器はショップの装備販売枠（kind=equipment。採掘ハンマーと同じ経路）で売り、
    // 初期装備と合わせて入手経路を保証する（docs/攻撃方法の実装案.md §6.5）
    const storyWeapons = asArray(items?.weapons).filter((weapon) => weapon?.storyGenerated === true);
    return {
        formatVersion: '1.0',
        mapId: startMapId,
        shopProfiles: [{
            id: `${startMapId}_story_shop`,
            enabledAreaTypes: [],
            commonPoolIds: [],
            exclusiveEntries: withEssentialShopEntries([
                ...storyItems.map((item, index) => ({
                    itemId: item.id,
                    buyPrice: clamp(Math.floor(Number(item.shopPrice) || 1000), 100, 100000),
                    sortOrder: 100 + index * 10,
                })),
                ...storyWeapons.map((weapon, index) => ({
                    itemId: weapon.id,
                    buyPrice: clamp(Math.floor(Number(weapon.shopPrice) || 5000), 100, 100000),
                    kind: 'equipment',
                    sortOrder: 500 + index * 10,
                })),
            ]),
            midnightEntries: [],
            blackmarketEntries: [],
            eventLimitedEntries: [],
        }],
    };
}

/**
 * 物語武器を equipment.json へマージする（storyGenerated の装備だけを入れ替え）。
 * 旧スキャフォールド由来で weapon スロットタイプが無いゲームには、sevenfold マスタ
 * （maps/_shared/equipment.json）と同一定義のスロットを移行シムとして補う。
 * @returns 変更数（0 なら書き込み不要）
 */
export function applyStoryWeaponsToEquipment(document, weapons) {
    if (!document || typeof document !== 'object' || Array.isArray(document)) return 0;
    let updated = 0;
    if (!Array.isArray(document.slotTypes)) document.slotTypes = [];
    if (weapons.length > 0 && !document.slotTypes.some((slot) => slot?.id === 'weapon')) {
        document.slotTypes.push({
            id: 'weapon',
            name: '武器',
            nameKey: 'equipment.slotTypes.weapon.name',
            maxSlots: 1,
        });
        updated++;
    }
    // owner / shopPrice は装備マスタには持ち込まない（owner は初期装備の割当専用、
    // 販売価格はショップ側 buyPrice が正）。iconId を持たない旧生成データには
    // delivery からアイコンを補う（「？」フォールバック防止。2026-08-01 指示）
    const iconIds = weaponCatalogConfig().iconIds ?? {};
    const entries = weapons.map(({ owner: _owner, shopPrice: _shopPrice, ...entry }) => ({
        ...entry,
        iconId: str(entry.iconId)
            || str(iconIds[entry.weaponSpec?.delivery])
            || (entry.weaponSpec?.delivery === 'burst' ? 'weapon_gun' : 'weapon_launcher'),
    }));
    const preserved = asArray(document.items).filter((item) => item?.storyGenerated !== true);
    const before = JSON.stringify(asArray(document.items).filter((item) => item?.storyGenerated === true));
    if (before !== JSON.stringify(entries)) updated++;
    document.items = [...preserved, ...entries];
    return updated;
}

/**
 * 物語武器の nameKey / descriptionKey に対応するロケールキーを欠落分だけ原文で埋める
 * （F5 i18n 検証の「マスタ由来キー欠落」対策）。正訳は ⑥-2 の i18n パッチが持ち、
 * 取り込み時のマージ（deepMergeInto）がこの原文を上書きする。既存キーには触れない。
 * @returns 追加したキー数（0 なら書き込み不要）
 */
export function applyStoryWeaponTranslationBackfill(localeDocument, weapons) {
    if (!localeDocument || typeof localeDocument !== 'object' || Array.isArray(localeDocument)) return 0;
    const messages = (localeDocument.messages ??= {});
    let added = 0;
    const setIfMissing = (dotPath, text) => {
        if (typeof dotPath !== 'string' || dotPath === '' || typeof text !== 'string') return;
        const segs = dotPath.split('.');
        let cur = messages;
        for (const seg of segs.slice(0, -1)) {
            if (cur[seg] !== undefined && (typeof cur[seg] !== 'object' || cur[seg] === null || Array.isArray(cur[seg]))) {
                return; // 既存の文字列キーと衝突する場合はスキップ（既存訳を壊さない）
            }
            cur = cur[seg] ??= {};
        }
        const leaf = segs[segs.length - 1];
        if (cur[leaf] === undefined) {
            cur[leaf] = text;
            added += 1;
        }
    };
    for (const weapon of asArray(weapons)) {
        if (weapon?.storyGenerated !== true) continue;
        setIfMissing(weapon?.nameKey, weapon?.name);
        setIfMissing(weapon?.descriptionKey, weapon?.description);
    }
    return added;
}

/**
 * アイテム一覧の「敵用」カテゴリが武器系効果（弾倉・投擲・設置）を含むことを保証する
 * （2026-08-01 指示: 弾倉や爆弾系の物理攻撃アイテムは「敵用」カテゴリへ統一）。
 * sevenfold マスタの item-categories.json は更新済みだが、スキャフォールド済みの既存ゲームには
 * 反映されないため、⑤-3/⑥-3 の再反映で欠落分だけを冪等に追加する。
 * @returns 追加した効果数（0 なら書き込み不要）
 */
export function ensureOffenseCategoryCoversWeaponEffects(document) {
    const category = asArray(document?.categories).find((entry) => entry?.id === 'offense');
    if (!category) return 0;
    const matcher = (category.matcher ??= {});
    if (!Array.isArray(matcher.effects)) matcher.effects = [];
    let added = 0;
    for (const effect of WEAPON_ITEM_EFFECTS) {
        if (!matcher.effects.includes(effect)) {
            matcher.effects.push(effect);
            added += 1;
        }
    }
    return added;
}

/**
 * ゲーム開始時に必ず持たせる弾薬の個数（weaponCatalog.initialAmmoCount）。
 * 設計プロンプトと初期所持品の生成が同じ値を見るための唯一の窓口（2026-08-01 指示）。
 */
export function storyInitialAmmoCount() {
    return Math.max(1, int(weaponCatalogConfig().initialAmmoCount, STORY_INITIAL_AMMO_FALLBACK));
}

/**
 * 武器の使い手（owner）へ初期装備（weapon スロット）と初期弾薬を保証する。
 * story 由来の weapon 装備行だけを毎回作り直し、既存の他スロット装備・所持品には触れない。
 *
 * 初期弾薬のルール（2026-08-01 指示）:
 *   - 弾薬を使う武器（fire_equipped_weapon の弾と ammoClass で対になる武器）を持つキャラは、
 *     必ず storyInitialAmmoCount() 個ちょうどを所持して開始する（多くても少なくてもこの値へ統一）
 *   - 付与できるのは**アイテムショップで購入できる弾薬だけ**。買い足せない弾を初期所持させると
 *     撃ち切った時点で武器が死に装備になるため、ショップ在庫に無い弾は付与しない（fail closed）
 *   - 対象外になった過去の story 弾薬は初期所持品から取り除く（武器を差し替えた再取り込み対策）
 *
 * @param {object} document characters.json（破壊的に更新）
 * @param {Array} weapons items.json の weapons[]（storyGenerated のもの）
 * @param {object} itemsDoc items.json
 * @param {{shops?: object|null, shopMaster?: object|null}} [shopData]
 *        販売状況の実データ。省略すると「買える弾が無い」扱いになり弾薬を付与しない
 * @returns 変更キャラ数（0 なら書き込み不要）
 */
export function ensureStoryWeaponLoadout(document, weapons, itemsDoc, shopData = {}) {
    const characters = Array.isArray(document?.characters) ? document.characters : [];
    if (characters.length === 0) return 0;
    const storyAmmoItems = asArray(itemsDoc?.items)
        .filter((item) => item?.storyGenerated === true
            && item?.effect === 'fire_equipped_weapon' && str(item?.ammoClass));
    const ammoIdByClass = new Map(storyAmmoItems.map((item) => [item.ammoClass, item.id]));
    const storyAmmoIds = new Set(storyAmmoItems.map((item) => item.id));
    const purchasableIds = collectPurchasableItemIds(shopData ?? {});
    const initialAmmoCount = storyInitialAmmoCount();
    let updated = 0;
    for (const character of characters) {
        let changed = false;
        const prevEquipment = asArray(character.initialEquipment);
        const preserved = prevEquipment
            .filter((entry) => !(entry?.slotId === 'weapon' && str(entry?.equipmentId).startsWith('story_')));
        const mine = weapons.filter((weapon) => weapon?.owner === character.id);
        // weapon スロットは maxSlots=1 のため、割当が複数あっても先頭だけを初期装備にする
        const weaponEntries = mine.slice(0, 1).map((weapon) => ({
            slotId: 'weapon', slotIndex: 0, equipmentId: weapon.id,
        }));
        const nextEquipment = [...preserved, ...weaponEntries];
        if (JSON.stringify(nextEquipment) !== JSON.stringify(prevEquipment)) {
            if (nextEquipment.length > 0) character.initialEquipment = nextEquipment;
            else delete character.initialEquipment;
            changed = true;
        }
        // このキャラが装備する武器の弾薬のうち、ショップで買えるものだけを初期所持にする
        const grantIds = new Set(mine
            .map((weapon) => ammoIdByClass.get(str(weapon?.weaponSpec?.ammoClass)))
            .filter((ammoId) => ammoId && purchasableIds.has(ammoId)));
        const prevItems = character.initialItems && typeof character.initialItems === 'object'
            ? character.initialItems
            : null;
        const nextItems = { ...(prevItems ?? {}) };
        for (const ammoId of storyAmmoIds) {
            if (!grantIds.has(ammoId)) delete nextItems[ammoId];
        }
        for (const ammoId of grantIds) nextItems[ammoId] = initialAmmoCount;
        if (JSON.stringify(nextItems) !== JSON.stringify(prevItems ?? {})) {
            if (Object.keys(nextItems).length > 0) character.initialItems = nextItems;
            else delete character.initialItems;
            changed = true;
        }
        if (changed) updated++;
    }
    return updated;
}

function designArea(design, areaId) {
    return design?.areas?.find((area) => area.id === areaId) ?? design?.areas?.[0] ?? null;
}

function toEngineBlockFill(fill) {
    const out = {
        id: fill.id,
        ...(fill.purpose ? { comment: fill.purpose } : {}),
        blockType: fill.blockType,
        posRelativeTo: 'origin',
        pos: cloneJson(fill.pos),
        heightMode: fill.heightMode ?? 'absolute',
    };
    if (fill.blockTypes?.length) out.blockTypes = cloneJson(fill.blockTypes);
    if (fill.shape === 'circle') {
        out.shape = 'circle';
        out.radius = fill.radius;
        out.height = fill.height;
    } else if (fill.shape === 'ellipse') {
        out.shape = 'ellipse';
        out.radiusX = fill.radiusX;
        out.radiusZ = fill.radiusZ;
        out.height = fill.height;
    } else {
        out.size = cloneJson(fill.size);
    }
    for (const key of ['scatterProbability', 'replaceOnly', 'requiresBelow', 'innerBlockType', 'shellThickness']) {
        if (fill[key] !== undefined) out[key] = cloneJson(fill[key]);
    }
    return out;
}

/** 設計の敷地矩形・機械変換された道路ストリップ・ランドマーク敷地（木立・小物の回避に使う予約領域）。 */
function collectReservedRects(design) {
    const engine = pipelineConfig().worldDesign.engine;
    const rects = [];
    for (const building of asArray(design.buildings)) {
        rects.push({
            minX: building.position[0] - 1, maxX: building.position[0] + building.width + 1,
            minZ: building.position[2] - 1, maxZ: building.position[2] + building.depth + 1,
        });
    }
    for (const cluster of asArray(design.buildingClusters)) rects.push({ ...cluster.bounds });
    for (const landmark of asArray(design.landmarks)) rects.push(landmarkFootprintRect(landmark, engine));
    const overworld = design.foundation.generator === 'overworld';
    for (const road of asArray(design.terrain?.roads)) {
        if (overworld && !road.range) continue; // 全域道路は地表判定側（designGroundInfo）が拾う
        const half = Math.ceil(Math.max(1, road.width) / 2);
        const range = road.range ?? [0, road.axis === 'z' ? design.scale.worldSize.width : design.scale.worldSize.depth];
        rects.push(road.axis === 'z'
            ? { minX: range[0], maxX: range[1], minZ: road.position - half, maxZ: road.position + half + 1 }
            : { minX: road.position - half, maxX: road.position + half + 1, minZ: range[0], maxZ: range[1] });
    }
    return rects;
}

/** groves（木立）を樹木 blockFill 群へ決定的展開する（void=絶対Y、overworld=地表追従）。 */
function buildGroveFills(design) {
    const engine = pipelineConfig().worldDesign.engine;
    const groveCfg = engine.grove;
    const placement = engine.placement;
    const overworld = design.foundation.generator === 'overworld';
    const roadLike = new Set(placement.roadLikeBlocks);
    const reservedRects = collectReservedRects(design);
    const fills = [];
    for (const grove of asArray(design.groves)) {
        const shape = grove.shape === 'rect'
            ? { kind: 'rect', ...grove.bounds }
            : { kind: 'circle', x: grove.center.x, z: grove.center.z, radius: grove.radius };
        const accept = (x, z) => {
            const info = designGroundInfo(design, x, z);
            if (!isWalkableSurface(info, placement.unwalkableSurfaceBlocks) || roadLike.has(info.blockType)) return false;
            const canopy = Math.max(...groveCfg.tree.canopyLayers.map((layer) => layer.radius));
            const treeRect = { minX: x - canopy, maxX: x + canopy + 1, minZ: z - canopy, maxZ: z + canopy + 1 };
            return !reservedRects.some((rect) => rectsIntersect(rect, treeRect));
        };
        const points = scatterPointsInShape({
            seedText: `grove_${grove.id}`,
            count: grove.treeCount,
            shape,
            minSpacing: groveCfg.minTreeSpacing,
            accept,
        });
        for (const [index, point] of points.entries()) {
            if (fills.length + 1 + groveCfg.tree.canopyLayers.length > groveCfg.maxGeneratedFills) return fills;
            const prefix = `${grove.id}_t${String(index + 1).padStart(2, '0')}`;
            const heightMode = overworld ? 'terrainSurface' : 'absolute';
            // terrainSurface は各カラムの地表からの相対 y、absolute は fills から計算した島の地表 y を使う
            const baseY = overworld ? 1 : (designGroundInfo(design, point.x, point.z)?.y ?? 0) + 1;
            fills.push({
                id: `${prefix}_trunk`, shape: 'rect', blockType: groveCfg.tree.trunkBlock,
                pos: [point.x, baseY, point.z], size: [1, grove.trunkHeight, 1], heightMode,
            });
            let layerY = baseY + grove.trunkHeight;
            for (const [layerIndex, layer] of groveCfg.tree.canopyLayers.entries()) {
                fills.push({
                    id: `${prefix}_leaf${layerIndex + 1}`, shape: 'circle', blockType: groveCfg.tree.leavesBlock,
                    pos: [point.x, layerY, point.z], radius: layer.radius, height: layer.height, heightMode,
                });
                layerY += layer.height;
            }
        }
    }
    return fills;
}

/**
 * kind=castle をエンジン城生成器の config へ合成する。
 * template（レガルス実績値）の主要寸法を scale で等比展開し、
 * 跳ね橋の絶対 Z・バルコニー階数など縮尺依存の値を再計算する。
 */
export function composeCastleConfig(landmark, engine) {
    const castleCfg = engine.landmark.castle;
    const template = cloneJson(castleCfg.template);
    const scale = landmark.scale;
    const scaled = (value) => Math.max(1, Math.round(value * scale));
    const config = {
        ...template,
        platform: { ...template.platform, size: scaled(template.platform.size) },
        mainKeep: {
            ...template.mainKeep,
            width: scaled(template.mainKeep.width),
            height: scaled(template.mainKeep.height),
            roofHeight: scaled(template.mainKeep.roofHeight),
            turretHeight: scaled(template.mainKeep.turretHeight),
            centralTowerHeight: scaled(template.mainKeep.centralTowerHeight),
        },
        cornerTowers: {
            ...template.cornerTowers,
            offset: scaled(template.cornerTowers.offset),
            height: scaled(template.cornerTowers.height),
            radius: Math.max(3, Math.round(template.cornerTowers.radius * scale)),
        },
        walls: { ...template.walls, radius: scaled(template.walls.radius), height: scaled(template.walls.height) },
        gatehouse: { ...template.gatehouse, gateZOffset: scaled(template.gatehouse.gateZOffset) },
        moat: {
            ...template.moat,
            innerRadius: scaled(template.moat.innerRadius),
            width: landmark.moat ? template.moat.width : 0,
        },
        courtyard: { ...template.courtyard, pathSize: scaled(template.courtyard.pathSize) },
        courtyardEntrance: {
            ...template.courtyardEntrance,
            startZOffset: scaled(template.courtyardEntrance.startZOffset),
            endZOffset: scaled(template.courtyardEntrance.endZOffset),
        },
    };
    // 跳ね橋: 城門から堀の外＋前庭まで届く絶対 Z（エンジンは endZ 絶対座標で受け取る）
    config.drawbridge = {
        ...template.drawbridge,
        endZ: landmark.positionZ + config.moat.innerRadius + config.moat.width + castleCfg.drawbridgeApronBlocks,
    };
    // バルコニー階数を縮尺後の天守階数に合わせて再計算する（余剰階は生成されず見た目が崩れるため）
    const floorCount = Math.max(2, Math.floor(config.mainKeep.height / config.mainKeep.floorInterval));
    config.structures = asArray(template.structures).map((structure) => (
        structure.type === 'balcony'
            ? {
                ...structure,
                geometry: {
                    ...structure.geometry,
                    floors: Array.from({ length: Math.max(1, floorCount - 1) }, (_, index) => index + 2),
                },
            }
            : structure
    ));
    return config;
}

/** kind=tower を中空シェル・窓帯・バルコニー・頂部様式つきの blockFill 群へ合成する。 */
export function composeTowerFills(landmark, design) {
    const world = design.scale.worldSize;
    const maxY = world.height - 1;
    const [x, z] = landmark.position;
    const ground = designGroundInfo(design, x, z);
    const baseY = clamp(landmark.altitude ?? ((ground?.y ?? 0) + 1), 1, maxY - 8);
    const materials = landmark.materials;
    const fills = [];
    // 土台（ノイズ・傾斜を均す2段のプリンス）
    fills.push({
        id: `${landmark.id}_plinth`, shape: 'circle', blockType: materials.accent,
        pos: [x, Math.max(0, baseY - 1), z], radius: landmark.baseRadius + 2, height: 2,
    });
    // 胴体: 3 セクションの中空テーパーシェル
    const sections = 3;
    let currentY = baseY + 1;
    let baseRadiusAtGround = landmark.baseRadius;
    for (let index = 0; index < sections && currentY < maxY; index++) {
        const t = index / sections;
        const radius = Math.max(2, Math.round(landmark.baseRadius + (landmark.topRadius - landmark.baseRadius) * t));
        if (index === 0) baseRadiusAtGround = radius;
        const sectionHeight = Math.min(Math.round(landmark.height / sections), maxY - currentY);
        if (sectionHeight < 1) break;
        fills.push({
            id: `${landmark.id}_shaft${index + 1}`, shape: 'circle', blockType: materials.wall,
            pos: [x, currentY, z], radius, height: sectionHeight, innerBlockType: 'AIR', shellThickness: 1,
        });
        if (landmark.balconies && index < sections - 1) {
            fills.push({
                id: `${landmark.id}_balcony${index + 1}`, shape: 'circle', blockType: materials.accent,
                pos: [x, currentY + sectionHeight, z], radius: radius + 1, height: 1,
            });
        }
        currentY += sectionHeight;
    }
    // 窓帯: floorHeight ごとに壁ブロックだけを窓へ置換（半径は最大で覆い replaceOnly で正確に効く）
    for (let y = baseY + 1 + landmark.floorHeight; y < currentY - 1; y += landmark.floorHeight) {
        fills.push({
            id: `${landmark.id}_win_${y}`, shape: 'circle', blockType: materials.window,
            pos: [x, y, z], radius: landmark.baseRadius + 1, height: 1, replaceOnly: [materials.wall],
        });
    }
    // 入口: 南面の壁を切り欠く（幅3×高4）
    fills.push({
        id: `${landmark.id}_entrance`, shape: 'rect', blockType: 'AIR',
        pos: [x - 1, baseY + 1, z + Math.max(1, baseRadiusAtGround - 2)], size: [3, 4, 4],
        replaceOnly: [materials.wall, materials.window],
    });
    // 頂部様式
    const topY = Math.min(currentY, maxY - 1);
    const topRadius = Math.max(2, landmark.topRadius);
    const topFills = {
        spire: [
            { radius: topRadius, height: 1, block: materials.wall },
            { radius: Math.max(1, topRadius - 1), height: 2, block: materials.wall },
            { radius: 1, height: 3, block: materials.accent },
        ],
        dome: [
            { radius: topRadius, height: 1, block: materials.accent },
            { radius: Math.max(1, topRadius - 1), height: 1, block: materials.accent },
            { radius: Math.max(1, topRadius - 2), height: 1, block: materials.accent },
        ],
        crystal: [
            { radius: Math.max(1, topRadius - 1), height: 2, block: materials.top },
            { radius: 1, height: 3, block: materials.top },
        ],
        beacon: [
            { radius: topRadius, height: 1, block: materials.accent },
            { radius: 1, height: 3, block: materials.top },
        ],
        crown: [
            { radius: topRadius + 1, height: 2, block: materials.wall, scatter: 0.6 },
        ],
        flat: [
            { radius: topRadius, height: 1, block: materials.accent },
        ],
    }[landmark.topStyle] ?? [];
    let topOffset = 0;
    for (const [index, layer] of topFills.entries()) {
        const y = topY + topOffset;
        if (y > maxY) break;
        fills.push({
            id: `${landmark.id}_top${index + 1}`, shape: 'circle', blockType: layer.block,
            pos: [x, y, z], radius: layer.radius, height: Math.min(layer.height, maxY - y + 1),
            ...(layer.scatter ? { scatterProbability: layer.scatter } : {}),
        });
        topOffset += layer.height;
    }
    return fills;
}

/**
 * landmarks を⑤の成果物へ展開する。
 * castle=城オブジェクト+terrain参照、tower/blueprint/custom=blockFill 群。
 * fill 総数は landmark.maxGeneratedFillsTotal で制限し、超過するランドマークは
 * 半端に欠けた建造物を作らないよう丸ごとスキップして notes に記録する。
 */
export function buildLandmarkArtifacts(design, { gameDir = null } = {}) {
    const engine = pipelineConfig().worldDesign.engine;
    const result = { fills: [], objects: [], castle: null, notes: [] };
    let budget = engine.landmark.maxGeneratedFillsTotal;
    for (const landmark of asArray(design.landmarks)) {
        if (landmark.kind === 'castle') {
            result.castle = { enabled: true, positionZ: landmark.positionZ };
            result.objects.push({
                id: landmark.id,
                type: 'castle',
                variant: 'story_main',
                posRelativeTo: 'castle',
                pos: [0, 0, 0],
                rot: 0,
                config: composeCastleConfig(landmark, engine),
            });
            continue;
        }
        let fills = [];
        if (landmark.kind === 'tower') {
            fills = composeTowerFills(landmark, design);
        } else if (landmark.kind === 'blueprint') {
            const blueprint = gameDir ? loadBlueprintDefinition(gameDir, landmark.blueprintId) : null;
            if (!blueprint) {
                result.notes.push(`ランドマーク ${landmark.id}: ブループリント ${landmark.blueprintId} を読み込めないためスキップ`);
                continue;
            }
            const ground = designGroundInfo(design, landmark.position[0], landmark.position[1]);
            const baseY = landmark.altitude ?? (ground ? ground.y + 1 : engine.landmark.floatingDefaultAltitude);
            fills = expandBlueprintFills({ landmark, blueprint, baseY, worldHeight: engine.world.height });
        } else {
            fills = asArray(landmark.fills);
        }
        if (fills.length > budget) {
            result.notes.push(`ランドマーク ${landmark.id}: fill 予算超過（${fills.length} > 残り ${budget}）のためスキップ`);
            continue;
        }
        budget -= fills.length;
        result.fills.push(...fills);
    }
    return result;
}

/** 設計から機械生成する追加 blockFill（範囲付き・void の道路、void の花畑、木立の樹木）。 */
function buildGeneratedFills(design) {
    const engine = pipelineConfig().worldDesign.engine;
    const placement = engine.placement;
    const overworld = design.foundation.generator === 'overworld';
    const fills = [];
    // 道路 → 敷石 fill。overworld は range 付きのみ（全域道路はエンジンが生成）。void は全道路を変換する。
    // replaceOnly の自然面制限により、島の外・水面上へは敷石が延びない
    for (const road of asArray(design.terrain?.roads)) {
        if (overworld && !road.range) continue;
        const width = Math.max(1, road.width);
        const half = Math.floor(width / 2);
        const range = road.range ?? [0, road.axis === 'z' ? design.scale.worldSize.width : design.scale.worldSize.depth];
        const pos = road.axis === 'z' ? [range[0], 0, road.position - half] : [road.position - half, 0, range[0]];
        const size = road.axis === 'z' ? [range[1] - range[0], 1, width] : [width, 1, range[1] - range[0]];
        if (!overworld) {
            // 道路中心線を等間隔サンプリングし、最頻の自然面 y を敷石高さにする
            // （中間の1点だけだと桟橋・橋など detailFill の上を拾い、replaceOnly で全欠落し得る）
            const naturals = new Set(placement.naturalSurfaceBlocks);
            const levels = new Map();
            for (let step = 0; step <= 8; step++) {
                const along = range[0] + Math.floor(((range[1] - 1 - range[0]) * step) / 8);
                const info = road.axis === 'z'
                    ? designGroundInfo(design, along, road.position)
                    : designGroundInfo(design, road.position, along);
                if (!info || !naturals.has(info.blockType)) continue;
                levels.set(info.y, (levels.get(info.y) ?? 0) + 1);
            }
            const best = [...levels.entries()].sort((a, b) => b[1] - a[1])[0];
            if (!best) continue;
            pos[1] = best[0];
        }
        fills.push({
            id: `${road.id}_paving`, purpose: road.description || '道路の敷石（設計 roads の自動反映）',
            shape: 'rect', blockType: road.blockType, pos, size,
            heightMode: overworld ? 'terrainSurface' : 'absolute',
            replaceOnly: [...placement.naturalSurfaceBlocks],
        });
    }
    // void の花畑 → 草地の上へだけ咲く花 fill（overworld はエンジンの flowerZones が生成する）
    if (!overworld) {
        const topY = maxFillTopY([...design.foundation.fills, ...design.detailFills], design.scale.worldSize.height);
        for (const zone of asArray(design.terrain?.vegetation?.flowerZones)) {
            const base = {
                id: `${zone.id}_bloom`, purpose: zone.description || '花畑（void への自動反映）',
                blockType: zone.types[0], blockTypes: [...zone.types],
                scatterProbability: zone.density, replaceOnly: ['AIR'], requiresBelow: ['GRASS'], heightMode: 'absolute',
            };
            if (zone.shape === 'rect') {
                fills.push({ ...base, shape: 'rect', pos: [zone.bounds.minX, 1, zone.bounds.minZ], size: [zone.bounds.maxX - zone.bounds.minX, topY + 1, zone.bounds.maxZ - zone.bounds.minZ] });
            } else {
                fills.push({ ...base, shape: 'circle', pos: [zone.center.x, 1, zone.center.z], radius: zone.radius, height: topY + 1 });
            }
        }
    }
    return fills;
}

/**
 * ⑤objects の LLM 出力に含まれる「設計外の小さな装飾 fill」を選別して残す。
 * 世界の地面・水域を覆い直す大きな fill（過去の不具合原因）は破棄し、
 * 看板・街灯・樽・花壇のような足面の小さい生活小物だけを設計 fills の後ろへ足す。
 */
function collectLlmExtraFills(json, design, usedIds) {
    const engine = pipelineConfig().worldDesign.engine;
    const placement = engine.placement;
    const center = engine.world.center;
    const safeBlocks = new Set(engine.safeBlocks);
    const extras = [];
    for (const fill of asArray(json.blockFills)) {
        if (extras.length >= placement.llmExtraFillMaxCount) break;
        if (!fill || typeof fill !== 'object') continue;
        const id = str(fill.id);
        if (!id || usedIds.has(id)) continue;
        const blockType = str(fill.blockType);
        if (!safeBlocks.has(blockType)) continue;
        // world_center 相対は絶対座標へ変換（エンジンの基準座標解決と同じ加算）
        const relative = str(fill.posRelativeTo);
        if (relative && !['origin', 'absolute', 'world_origin', 'world_center'].includes(relative)) continue;
        const offset = relative === 'world_center' ? { x: center.x, z: center.z } : { x: 0, z: 0 };
        const pos = asArray(fill.pos);
        const shape = ['circle', 'ellipse'].includes(fill.shape) ? fill.shape : 'rect';
        const footprint = shape === 'circle'
            ? (2 * Math.max(1, Number(fill.radius) || 1) + 1) ** 2
            : shape === 'ellipse'
                ? (2 * Math.max(1, Number(fill.radiusX) || 1) + 1) * (2 * Math.max(1, Number(fill.radiusZ) || 1) + 1)
                : Math.max(1, Number(asArray(fill.size)[0]) || 1) * Math.max(1, Number(asArray(fill.size)[2]) || 1);
        if (footprint > placement.llmExtraFillMaxFootprint) continue;
        const converted = {
            ...cloneJson(fill),
            id,
            pos: [
                Math.floor((Number(pos[0]) || 0) + offset.x),
                Math.max(0, Math.floor(Number(pos[1]) || 0)),
                Math.floor((Number(pos[2]) || 0) + offset.z),
            ],
            posRelativeTo: 'origin',
        };
        usedIds.add(id);
        extras.push(converted);
    }
    return extras;
}

function expandDesignedBuildings(json, design) {
    const engine = pipelineConfig().worldDesign.engine;
    const cfg = engine.building;
    const placement = engine.placement;
    const overworld = design.foundation.generator === 'overworld';
    const roadLike = new Set(placement.roadLikeBlocks);
    const waterLike = new Set(placement.waterLikeBlocks);
    const generated = asArray(json.objects);
    const generatedBuildings = generated.filter((entry) => entry?.type === 'building');
    const otherObjects = generated.filter((entry) => entry?.type !== 'building');
    const defaults = cfg.defaultMaterials;
    const occupied = [];
    const overlaps = (x, z, width, depth) => occupied.some((rect) => (
        x < rect.x + rect.width && x + width > rect.x && z < rect.z + rect.depth && z + depth > rect.z
    ));

    const landmarkRects = asArray(design.landmarks).map((landmark) => landmarkFootprintRect(landmark, engine));
    /** 敷地矩形が道路・水路・川・環状水路・ランドマーク敷地・水／道系 detailFill と重なるか */
    const hitsReservedZone = (rect, clusterArea) => {
        const terrain = design.terrain;
        if (landmarkRects.some((landmarkRect) => rectsIntersect(landmarkRect, rect))) return true;
        for (const road of asArray(terrain.roads)) {
            if (roadIntersectsRect(road, rect, placement.buildingRoadMargin)) return true;
        }
        for (const canal of asArray(terrain.water?.canals)) {
            if (canalIntersectsRect(canal, rect, placement.buildingWaterMargin)) return true;
        }
        for (const river of asArray(terrain.water?.rivers)) {
            if (rectsIntersect({
                minX: river.xRange[0] - placement.buildingWaterMargin, maxX: river.xRange[1] + placement.buildingWaterMargin,
                minZ: river.zRange[0] - placement.buildingWaterMargin, maxZ: river.zRange[1] + placement.buildingWaterMargin,
            }, rect)) return true;
        }
        for (const ring of asArray(terrain.water?.ringCanals)) {
            const ringCenter = { x: engine.world.center.x, z: engine.world.center.z };
            if (ringIntersectsRect(ringCenter, ring.radius, Math.max(ring.bankHalfWidth, ring.halfWidth), rect, placement.buildingWaterMargin)) return true;
        }
        for (const fill of asArray(design.detailFills)) {
            if (Number.isFinite(Number(fill.scatterProbability)) && Number(fill.scatterProbability) < 1) continue;
            if (!waterLike.has(fill.blockType) && !roadLike.has(fill.blockType)) continue;
            const footprint = fill.shape === 'circle'
                ? (2 * fill.radius + 1) ** 2
                : fill.shape === 'ellipse' ? (2 * fill.radiusX + 1) * (2 * fill.radiusZ + 1) : fill.size[0] * fill.size[2];
            if (clusterArea && footprint > clusterArea * placement.clusterFillCoverageSkipRatio) continue; // 全域舗装などの背景 fill は敷地判定から除外
            if (fillFootprintIntersectsRect(fill, rect)) return true;
        }
        return false;
    };

    /** 敷地の四隅＋中心の地表が水・道路でなく同じ高さか。OK なら基準地表 y を返す */
    const buildableSurfaceY = (x, z, width, depth) => {
        const points = [
            [x, z], [x + width - 1, z], [x, z + depth - 1], [x + width - 1, z + depth - 1],
            [x + Math.floor(width / 2), z + Math.floor(depth / 2)],
        ];
        let surfaceY = null;
        for (const [px, pz] of points) {
            const info = designGroundInfo(design, px, pz);
            if (!isWalkableSurface(info, placement.unwalkableSurfaceBlocks) || roadLike.has(info.blockType)) return null;
            if (surfaceY === null) surfaceY = info.y;
            else if (info.y !== surfaceY) return null; // 段差・崖をまたぐ敷地は不可
        }
        return surfaceY;
    };

    // overworld はエンジン自動接地（pos[1]=0 で地形高さへ）、void は fills 地表 +1（実例 sample_town と同じ規約）
    const buildingPosY = (surfaceY) => (overworld ? 0 : surfaceY + 1);

    /** 最寄りの道路へ玄関を向ける（見つからなければ null = 既定の south） */
    const doorSideFor = (x, z, width, depth) => {
        let best = null;
        let bestDist = placement.doorFaceSearchDistance + 1;
        const cx = x + width / 2;
        const cz = z + depth / 2;
        for (const road of asArray(design.terrain.roads)) {
            const half = Math.max(1, road.width) / 2;
            if (road.axis === 'x') {
                if (road.range && (cz < road.range[0] || cz >= road.range[1])) continue;
                const dist = Math.abs(cx - road.position) - half;
                if (dist < bestDist) {
                    bestDist = dist;
                    best = road.position < cx ? 'west' : 'east';
                }
            } else {
                if (road.range && (cx < road.range[0] || cx >= road.range[1])) continue;
                const dist = Math.abs(cz - road.position) - half;
                if (dist < bestDist) {
                    bestDist = dist;
                    best = road.position < cz ? 'north' : 'south';
                }
            }
        }
        return best;
    };

    const baseBuilding = (source, values) => {
        const look = values.look ?? {};
        const entry = {
            ...(source ?? {}),
            id: values.id,
            type: 'building',
            buildingType: source?.buildingType || cfg.defaultType,
            pos: values.pos,
            width: values.width,
            depth: values.depth,
            floors: values.floors,
            // 素材は 設計（物語のパレット） > ⑤LLM > 既定材 の優先で決める
            wallBlock: look.wallBlock || source?.wallBlock || defaults.wallBlock,
            roofBlock: look.roofBlock || source?.roofBlock || defaults.roofBlock,
            floorBlock: look.floorBlock || source?.floorBlock || defaults.floorBlock,
            windowBlock: look.windowBlock || source?.windowBlock || defaults.windowBlock,
            doorCount: Number.isFinite(Number(source?.doorCount)) ? source.doorCount : 1,
            doorSide: values.doorSide || source?.doorSide || 'south',
        };
        if (Number.isFinite(Number(look.floorHeight))) entry.floorHeight = look.floorHeight;
        if (look.flatRoof === true) entry.openTop = true; // 陸屋根（現代ビル）: 尖り屋根を作らない
        return entry;
    };
    const buildings = [];
    for (const [index, plan] of asArray(design.buildings).entries()) {
        const source = generatedBuildings.find((entry) => entry.id === plan.id) ?? generatedBuildings[index] ?? null;
        // 主要建築は設計座標を尊重し、高さだけ地表へ合わせる（水上などは lint が④再設計で検出済み）
        const surfaceY = buildableSurfaceY(plan.position[0], plan.position[2], plan.width, plan.depth);
        const posY = surfaceY === null ? (overworld ? 0 : plan.position[1]) : buildingPosY(surfaceY);
        const entry = baseBuilding(source, {
            id: plan.id,
            pos: [plan.position[0], posY, plan.position[2]],
            width: plan.width,
            depth: plan.depth,
            floors: plan.floors,
            look: plan,
            doorSide: doorSideFor(plan.position[0], plan.position[2], plan.width, plan.depth),
        });
        buildings.push(entry);
        occupied.push({ x: entry.pos[0], z: entry.pos[2], width: entry.width, depth: entry.depth });
    }
    for (const cluster of asArray(design.buildingClusters)) {
        const stepX = cluster.buildingWidth + cluster.spacing;
        const stepZ = cluster.buildingDepth + cluster.spacing;
        const jitterMax = Math.max(0, Math.floor((cluster.spacing - 2) / 2));
        const clusterArea = (cluster.bounds.maxX - cluster.bounds.minX) * (cluster.bounds.maxZ - cluster.bounds.minZ);
        const floorSpan = Math.max(1, cluster.floorsMax - cluster.floorsMin + 1);
        const seed = hashSeed(`cluster_${cluster.id}`);
        let created = 0;
        let cell = 0;
        for (let z = cluster.bounds.minZ; z + cluster.buildingDepth <= cluster.bounds.maxZ && created < cluster.count; z += stepZ) {
            for (let x = cluster.bounds.minX; x + cluster.buildingWidth <= cluster.bounds.maxX && created < cluster.count; x += stepX) {
                cell++;
                // 機械的な格子感を避ける決定的ジッター（間隔-2 の範囲内なので隣接建物とは重ならない）
                const jitterX = jitterMax > 0 ? (hash2D(seed, cell, 11) % (jitterMax * 2 + 1)) - jitterMax : 0;
                const jitterZ = jitterMax > 0 ? (hash2D(seed, cell, 29) % (jitterMax * 2 + 1)) - jitterMax : 0;
                const bx = clamp(x + jitterX, cluster.bounds.minX, Math.max(cluster.bounds.minX, cluster.bounds.maxX - cluster.buildingWidth));
                const bz = clamp(z + jitterZ, cluster.bounds.minZ, Math.max(cluster.bounds.minZ, cluster.bounds.maxZ - cluster.buildingDepth));
                if (overlaps(bx, bz, cluster.buildingWidth, cluster.buildingDepth)) continue;
                if (hitsReservedZone({ minX: bx - 1, maxX: bx + cluster.buildingWidth + 1, minZ: bz - 1, maxZ: bz + cluster.buildingDepth + 1 }, clusterArea)) continue;
                const surfaceY = buildableSurfaceY(bx, bz, cluster.buildingWidth, cluster.buildingDepth);
                if (surfaceY === null) continue;
                const source = generatedBuildings.find((entry) => entry.id === `${cluster.id}_${String(created + 1).padStart(2, '0')}`)
                    ?? generatedBuildings[(buildings.length + created) % Math.max(1, generatedBuildings.length)]
                    ?? null;
                const entry = baseBuilding(source, {
                    id: `${cluster.id}_${String(created + 1).padStart(2, '0')}`,
                    pos: [bx, buildingPosY(surfaceY), bz],
                    width: cluster.buildingWidth,
                    depth: cluster.buildingDepth,
                    floors: cluster.floorsMin + (hash2D(seed, cell, 47) % floorSpan),
                    look: cluster,
                    doorSide: doorSideFor(bx, bz, cluster.buildingWidth, cluster.buildingDepth),
                });
                buildings.push(entry);
                occupied.push({ x: bx, z: bz, width: entry.width, depth: entry.depth });
                created++;
            }
        }
    }
    json.objects = [...buildings, ...otherObjects];
    json.totalCount = json.objects.length;
    json.buildingTypeSettings ??= {};
    for (const building of buildings) {
        json.buildingTypeSettings[building.buildingType] ??= cloneJson(cfg.defaultTypeSettings);
    }
}

function applyNpcHabitats(json, design) {
    // posOffset の Y は多層マップで正しい地表を探すための探索アンカーとして保持する。
    // 実際の足元はエンジンがその近傍のボクセル地面へスナップする。
    for (const npc of asArray(json.npcs)) {
        if (!npc || typeof npc !== 'object') continue;
        const placement = design.npcPlacements.find((entry) => entry.castId === npc.id);
        const pos = asArray(placement ? placement.position : (npc.pos ?? npc.posOffset));
        if (pos.length < 3) continue;
        npc.posOffset = [int(pos[0], 0), int(pos[1], 0), int(pos[2], 0)];
        delete npc.pos;
        delete npc.posRelativeTo;
    }
}

function positiveNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
}

/** visualProfile からモデル内部単位に依存しない物理身長を取得する。 */
function profilePhysicalHeight(profile, role) {
    const pipeline = pipelineConfig();
    const visualCfg = pipeline.imagePrompts.visualProfile;
    let physicalHeight = positiveNumber(profile?.heightMeters);
    if (physicalHeight == null) {
        const normalizedHeight = positiveNumber(profile?.game?.displayHeight);
        const perMeter = positiveNumber(visualCfg.gameDisplayHeightPerMeter);
        if (normalizedHeight != null && perMeter != null) {
            physicalHeight = normalizedHeight / perMeter;
        }
    }
    physicalHeight ??= positiveNumber(visualCfg.defaultHeightMetersByRole?.[role]);
    return physicalHeight;
}

/** プレイアブル／人型リグGLBが使う正規化済みワールド表示高を取得する。 */
function profileRuntimeDisplayHeight(profile, role) {
    const normalizedHeight = positiveNumber(profile?.game?.displayHeight);
    if (normalizedHeight != null) return normalizedHeight;
    const pipeline = pipelineConfig();
    const perMeter = positiveNumber(pipeline.imagePrompts.visualProfile.gameDisplayHeightPerMeter);
    const physicalHeight = profilePhysicalHeight(profile, role);
    return physicalHeight != null && perMeter != null ? physicalHeight * perMeter : null;
}

function roundVisualHeight(value, placementCfg) {
    const digits = Number.isInteger(placementCfg.roundDigits) ? placementCfg.roundDigits : 4;
    const factor = 10 ** digits;
    return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** 主人公を優先し、人型リグ敵と同じワールド単位でプレイヤー表示高を決定する。 */
function playerReferenceDisplayHeight(analysis, castModels) {
    const pipeline = pipelineConfig();
    const placementCfg = pipeline.generatedCastVisualScale ?? {};
    const configuredRoles = asArray(placementCfg.playerReferenceRoles).filter((role) => typeof role === 'string' && role);
    const roles = configuredRoles.length > 0 ? configuredRoles : ['protagonist'];
    const casts = asArray(analysis?.cast);
    for (const role of roles) {
        const cast = casts.find((entry) => entry?.role === role);
        const model = cast?.id ? castModels?.get(cast.id) : null;
        const roleModel = model ?? [...(castModels?.values?.() ?? [])].find((entry) => entry?.role === role);
        const height = profileRuntimeDisplayHeight(roleModel?.visualProfile, role);
        if (height != null) return height;
    }
    for (const role of roles) {
        const height = profileRuntimeDisplayHeight(null, role);
        if (height != null) return height;
    }
    return null;
}

function enemyPlacementDisplayScale(castId, generatedEnemies) {
    const entry = asArray(generatedEnemies?.enemies).find((candidate) => candidate?.type === castId
        || candidate?.id === castId
        || String(candidate?.id ?? '').startsWith(`${castId}_`));
    return positiveNumber(entry?.displayScale) ?? 1;
}

function humanoidEnemyHeightRatio(placementCfg) {
    const ratioCfg = placementCfg.humanoidEnemyFinalHeightRatioToPlayer ?? {};
    const min = positiveNumber(ratioCfg.min) ?? 1;
    return { min, max: Math.max(min, positiveNumber(ratioCfg.max) ?? min) };
}

/**
 * キャスト種別ごとの表示高へ変換する。
 * 人型敵だけはプレイアブルと同じ正規化ワールド単位を使い、物理mを直接渡さない。
 * 非人型敵は見た目が成立している従来の物理身長倍率を維持する。
 */
function profileDisplayHeight(profile, role, humanoid = true, options = {}) {
    const physicalHeight = profilePhysicalHeight(profile, role);
    const pipeline = pipelineConfig();
    const placementCfg = pipeline.generatedCastVisualScale ?? {};
    const npcMultiplier = positiveNumber(placementCfg.heightMultiplier?.npc) ?? 1;
    if (role === 'npc') {
        return physicalHeight == null ? null : roundVisualHeight(physicalHeight * npcMultiplier, placementCfg);
    }
    if (!humanoid) {
        const multiplier = positiveNumber(placementCfg.heightMultiplier?.nonHumanoidEnemy) ?? 1;
        return physicalHeight == null ? null : roundVisualHeight(physicalHeight * multiplier, placementCfg);
    }

    const displayHeight = profileRuntimeDisplayHeight(profile, role);
    if (displayHeight == null) return null;
    const playerHeight = positiveNumber(options.playerReferenceDisplayHeight);
    const ratio = humanoidEnemyHeightRatio(placementCfg);
    const placementScale = positiveNumber(options.placementScale) ?? 1;
    if (playerHeight == null) return roundVisualHeight(displayHeight, placementCfg);
    const finalHeight = clamp(
        displayHeight * placementScale,
        playerHeight * ratio.min,
        playerHeight * ratio.max,
    );
    return roundVisualHeight(finalHeight / placementScale, placementCfg);
}

function npcTargetHeight(model, cfg) {
    if (model?.role === 'npc' && model.hasModel && model.glbPath) {
        const generatedHeight = profileDisplayHeight(model.visualProfile, 'npc', true);
        if (generatedHeight != null) return generatedHeight;
    }
    const placementCfg = pipelineConfig().generatedCastVisualScale ?? {};
    const multiplier = positiveNumber(placementCfg.heightMultiplier?.npc) ?? 1;
    const baseHeight = positiveNumber(model?.visualProfile?.heightMeters)
        ?? positiveNumber(cfg.npcFallbackTargetHeight)
        ?? 1.65;
    const digits = Number.isInteger(placementCfg.roundDigits) ? placementCfg.roundDigits : 4;
    const factor = 10 ** digits;
    return Math.round((baseHeight * multiplier + Number.EPSILON) * factor) / factor;
}

/**
 * 大型NPC用の接地・干渉探索footprintを返す（本家Sevenfold npcs.json契約）。
 * targetHeight（ワールド身長メートル）が閾値以上の静的NPCだけへ出力し、
 * ランタイムの近傍歩行可能地面探索が実寸で壁・天井との干渉を判定できるようにする。
 */
function npcSpawnFootprintFor(targetHeight, cfg) {
    const fp = cfg?.npcSpawnFootprint;
    const minHeight = positiveNumber(fp?.minTargetHeightMeters);
    const width = positiveNumber(fp?.width);
    const margin = Number(fp?.heightMarginMeters);
    const depth = positiveNumber(fp?.depth);
    const height = positiveNumber(targetHeight);
    if (minHeight == null || width == null || depth == null || !Number.isFinite(margin)) return null;
    if (height == null || height < minHeight) return null;
    return { width, height: height + margin, depth };
}

/**
 * 解析済み npc cast の配置漏れを補完する（全キャスト必須配置・2026-07-13 ユーザー要件）。
 * ⑤LLM が出力しなかった NPC を、④設計（自動補完済み）の位置と既定の会話で追加する。
 */
function backfillDesignedNpcs(json, design, analysis, castModels) {
    const cfg = pipelineConfig().worldDesign.engine.placement.castPlacement;
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    const fallbackDialogues = asArray(cfg.npcFallbackDialoguesByLanguage?.[language]);
    json.npcs = asArray(json.npcs);
    const present = new Set(json.npcs.map((npc) => npc?.id));
    for (const placement of design.npcPlacements) {
        if (present.has(placement.castId)) continue;
        const cast = asArray(analysis?.cast).find((entry) => entry.id === placement.castId);
        const name = localizedForGeneration(cast?.name, language, placement.castId);
        const model = castModels?.get(placement.castId);
        const targetHeight = npcTargetHeight(model, cfg);
        const spawnFootprint = npcSpawnFootprintFor(targetHeight, cfg);
        json.npcs.push({
            id: placement.castId,
            name,
            modelPath: cfg.npcFallbackModelPath,
            targetHeight,
            pos: cloneJson(placement.position),
            dialogues: fallbackDialogues.map((line) => line.replaceAll('{name}', name)),
            ...(spawnFootprint ? { spawnFootprint } : {}),
        });
        present.add(placement.castId);
    }
}

/**
 * 決定的補完（backfillDesignedNpcs）が持ち込む日本語原文（NPC名・既定会話）の英訳を、
 * ゲームロケールの exact 辞書へ不足分だけ登録する（ja原文とen辞書のセット更新規約）。
 * ⑤-2実行時点のi18nパッチに存在しない後付け文字列が、取り込み検証（validate-i18n）で
 * 未翻訳扱いになって取り込みジョブ全体を失敗させるのを防ぐ。既存の訳は上書きしない。
 */
export function ensureBackfillNpcExactTranslations(gameDir, design, analysis) {
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    if (language === 'en') return 0; // 英語生成の会話は日本語原文を持ち込まない
    const localePath = path.join(gameDir, 'maps/_shared/i18n/locales/en.json');
    const locale = readJsonIfExists(localePath);
    if (!locale || typeof locale !== 'object') return 0;
    const cfg = pipelineConfig().worldDesign.engine.placement.castPlacement;
    const jaLines = asArray(cfg.npcFallbackDialoguesByLanguage?.ja);
    const enLines = asArray(cfg.npcFallbackDialoguesByLanguage?.en);
    if (!locale.exact || typeof locale.exact !== 'object') locale.exact = {};
    const exact = locale.exact;
    let added = 0;
    const addMissing = (ja, en) => {
        if (typeof ja !== 'string' || ja.trim() === '' || exact[ja] !== undefined) return;
        exact[ja] = str(en) || ja;
        added++;
    };
    for (const placement of asArray(design?.npcPlacements)) {
        const cast = asArray(analysis?.cast).find((entry) => entry.id === placement.castId);
        const jaName = localizedForGeneration(cast?.name, 'ja', placement.castId);
        const enName = localizedForGeneration(cast?.name, 'en', placement.castId);
        addMissing(jaName, enName);
        jaLines.forEach((line, index) => {
            if (typeof line !== 'string') return;
            addMissing(
                line.replaceAll('{name}', jaName),
                str(enLines[index] ?? line).replaceAll('{name}', enName),
            );
        });
    }
    if (added > 0) writeJson(localePath, locale);
    return added;
}

/**
 * ④設計の重要地点イベント（applyImportantLocationEvents が注入する通知文）の日本語原文を、
 * en.json exact へ不足分だけ登録する。
 * この注入は 2026-07-14（74acecd）導入のため、それ以前に⑤-2 を実行した案件の i18n パッチには
 * 通知文の英訳が存在せず、取り込み検証（validate-i18n）が必ず失敗していた。
 * 対訳が無い場合は i18n パッチ生成と同じ「原文のまま登録」の慣例で自己修復し、
 * ⑤-2 再生成（キー単位マージで上書き）や en.json exact の手修正で正訳へ差し替え可能にする。
 */
export function ensureImportantLocationEventTranslations(gameDir, design, analysis) {
    const none = { added: 0, identityTexts: [] };
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    if (language === 'en') return none; // 英語生成の通知文は日本語原文を持ち込まない
    const ideas = asArray(design?.eventIdeas);
    if (ideas.length === 0) return none;
    const localePath = path.join(gameDir, 'maps/_shared/i18n/locales/en.json');
    const locale = readJsonIfExists(localePath);
    if (!locale || typeof locale !== 'object') return none;
    if (!locale.exact || typeof locale.exact !== 'object') locale.exact = {};
    const exact = locale.exact;
    const identityTexts = [];
    for (const event of ideas) {
        // storyEventActions の notification 分岐と同じ優先順（message → summary）。
        // dialogue 表示は lines（messageId 経由）のため exact の raw 検証対象にならない。
        if (event?.presentation === 'dialogue') continue;
        const text = str(event?.message) || str(event?.summary);
        if (!text || exact[text] !== undefined) continue;
        exact[text] = text;
        identityTexts.push(text);
    }
    if (identityTexts.length > 0) writeJson(localePath, locale);
    return { added: identityTexts.length, identityTexts };
}

// ── 相方プレイアブルの AI チャット NPC（companion 人格BOT・本家 Sevenfold §16 契約） ─────

function companionConfig() {
    return pipelineConfig().worldDesign.engine.placement.castPlacement.companion ?? null;
}

/**
 * 名前ラベル・HPバーの「頭上マージン」（実身長に加算するワールド単位）。
 * 正本は imagePrompts.visualProfile.headLabelWorldYMarginMeters（プレイアブル／companion 共通）。
 * 旧配置（castPlacement.companion.headLabelWorldYMarginMeters）も互換で読む。
 */
function headLabelWorldYMarginMeters() {
    return positiveNumber(pipelineConfig().imagePrompts?.visualProfile?.headLabelWorldYMarginMeters)
        ?? positiveNumber(companionConfig()?.headLabelWorldYMarginMeters)
        ?? 0.3;
}

/**
 * プレイヤー操作中キャラ専用の頭上マージン。プレイヤーは常にカメラ近くにいて同じ世界距離の
 * 隙間が大きく見える（perspective）ため、NPC 用より小さい値を既定にする（未指定は NPC 用と同値）。
 */
function playerHeadLabelWorldYMarginMeters() {
    return positiveNumber(pipelineConfig().imagePrompts?.visualProfile?.playerHeadLabelWorldYMarginMeters)
        ?? headLabelWorldYMarginMeters();
}

/** characters.json エントリからキャラ実身長（m）を解決する（visualProfile → collider → 既定1.7）。 */
function characterHeightMeters(entry) {
    return positiveNumber(entry?.visualProfile?.heightMeters)
        ?? positiveNumber(entry?.collider?.height)
        ?? 1.7;
}

/**
 * characters.json の全キャラへ headLabelWorldYAboveOrigin（実身長+マージン）を決定的に反映する。
 * プレイアブルの HP バーが、companion NPC と同じ「そのキャラの頭上」規則で表示されるようにする
 * （Meshy リグ済み GLB のバインドポーズ bbox 崩れで、バーが顔・頭に重なる問題の恒久対策）。
 * visualProfile 同期済みなら個別身長、未同期の旧案件は collider / 既定身長から一律に決まる（冪等）。
 */
export function ensurePlayableHeadLabelWorldY(gameDir) {
    const charactersPath = path.join(gameDir, 'maps/_shared/characters.json');
    const document = readJsonIfExists(charactersPath);
    if (!document || !Array.isArray(document.characters)) return 0;
    const margin = headLabelWorldYMarginMeters();
    const playerMargin = playerHeadLabelWorldYMarginMeters();
    let updated = 0;
    for (const character of document.characters) {
        if (!character || typeof character !== 'object') continue;
        const heightMeters = characterHeightMeters(character);
        const worldY = Math.round((heightMeters + margin) * 100) / 100;
        // プレイヤー操作中用（カメラ近接の perspective 補正ぶん小さめ）。エンジンはこちらを優先参照する
        const playerWorldY = Math.round((heightMeters + playerMargin) * 100) / 100;
        if (character.headLabelWorldYAboveOrigin !== worldY) {
            character.headLabelWorldYAboveOrigin = worldY;
            updated += 1;
        }
        if (character.playerHeadLabelWorldYAboveOrigin !== playerWorldY) {
            character.playerHeadLabelWorldYAboveOrigin = playerWorldY;
            updated += 1;
        }
    }
    if (updated > 0) writeJson(charactersPath, document);
    return updated;
}

/**
 * プレイアブル 2 キャラの「選択キャラ id（characters.json / スタートメニューの id）」と
 * 解析 cast（protagonist / partner）の対応を解決する。
 * companion の dialogueVariants / npc-personas.json variants のキーは選択キャラ id。
 */
export function resolvePlayablePair(analysis) {
    const characters = analysis?.game?.characters;
    const protagonistSlot = analysis?.game?.protagonist === 'girl' ? 'girl' : 'boy';
    const partnerSlot = protagonistSlot === 'girl' ? 'boy' : 'girl';
    const protagonistCharId = str(characters?.[protagonistSlot]?.id);
    const partnerCharId = str(characters?.[partnerSlot]?.id);
    if (!protagonistCharId || !partnerCharId || protagonistCharId === partnerCharId) return null;
    const cast = asArray(analysis?.cast);
    return {
        protagonist: {
            charId: protagonistCharId,
            name: characters[protagonistSlot].name,
            cast: cast.find((entry) => entry?.role === 'protagonist') ?? null,
        },
        partner: {
            charId: partnerCharId,
            name: characters[partnerSlot].name,
            cast: cast.find((entry) => entry?.role === 'partner') ?? null,
        },
    };
}

/**
 * companion NPC（npcs.json エントリ）を組み立てる。
 * ログインしていない側のプレイアブルキャラを、プレイヤースポーン付近の安全な場所へ
 * 配置する（type: "companion" + dialogueVariants。エンジンが選択キャラで相方へ切替え、
 * 足元 Y は現在の地面へ自動再解決する）。挨拶は①解析の aiPersona.greetings を優先し、
 * 無い旧案件は config の既定文（{name}=話しかける側キャラ名）で補う。
 */
export function buildCompanionNpcDef({ analysis, design, gameDir }) {
    const cfg = companionConfig();
    const pair = resolvePlayablePair(analysis);
    const spawn = asArray(design?.spawn);
    if (!cfg || !pair || spawn.length < 3) return null;
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    const charactersJson = gameDir ? readJsonIfExists(path.join(gameDir, 'maps/_shared/characters.json')) : null;
    const fallbackHeight = positiveNumber(cfg.fallbackTargetHeight) ?? 0.02;
    const labelMarginMeters = headLabelWorldYMarginMeters();
    const characterEntryOf = (charId) => asArray(charactersJson?.characters).find((c) => c?.id === charId) ?? null;
    const offset = asArray(cfg.spawnOffset);
    const pos = [0, 1, 2].map((i) => (Math.round(Number(spawn[i])) || 0) + (Math.round(Number(offset[i])) || 0));
    const faceYawOffsetDeg = Number.isFinite(Number(cfg.faceYawOffsetDeg)) ? Number(cfg.faceYawOffsetDeg) : 90;
    const variantFor = (player, companion) => {
        const playerName = localizedForGeneration(player.name, language, player.charId);
        const companionName = localizedForGeneration(companion.name, language, companion.charId);
        const entry = characterEntryOf(companion.charId);
        // 名前ラベル・ゲージはワールド固定オフセット（足元原点＋実身長＋マージン）で頭上へ置く。
        // characters.json に反映済みの値（ensurePlayableHeadLabelWorldY / ③-1 同期）を最優先し、
        // プレイアブル時の HP バーと companion 時のバーが常に同じ高さになるよう統一する。
        const headLabelWorldYAboveOrigin = positiveNumber(entry?.headLabelWorldYAboveOrigin)
            ?? Math.round((characterHeightMeters(entry) + labelMarginMeters) * 100) / 100;
        const ownGreetings = asArray(companion.cast?.aiPersona?.greetings).map((line) => str(line)).filter(Boolean);
        const dialogues = ownGreetings.length > 0
            ? ownGreetings
            : asArray(cfg.greetingsByLanguage?.[language]).map((line) => str(line).replaceAll('{name}', playerName)).filter(Boolean);
        return {
            name: companionName,
            modelPath: companion.charId,
            targetHeight: positiveNumber(entry?.displayHeight) ?? fallbackHeight,
            headLabelWorldYAboveOrigin,
            behavior: { faceYawOffsetDeg },
            dialogues,
        };
    };
    return {
        id: str(cfg.npcId) || 'companion',
        type: 'companion',
        dynamicModel: true,
        pos,
        behavior: { type: 'stationary', facingMode: 'lookAtPlayer' },
        dialogueVariants: {
            [pair.protagonist.charId]: variantFor(pair.protagonist, pair.partner),
            [pair.partner.charId]: variantFor(pair.partner, pair.protagonist),
        },
    };
}

/** npcs.json へ companion（相方 AI チャット NPC）を決定的に upsert する（再実行で常に最新契約へ揃える）。 */
function upsertCompanionNpc(json, { analysis, design, gameDir }) {
    const def = buildCompanionNpcDef({ analysis, design, gameDir });
    if (!def) return;
    json.npcs = asArray(json.npcs).filter((npc) => !(npc?.id === def.id || npc?.type === 'companion'));
    json.npcs.push(def);
}

/**
 * 相方 AI チャット NPC の npc-personas.json（本家 §16.6 契約）を組み立てる。
 * variants のキーは「選択中のプレイアブルキャラ id」、persona 本文は「相方キャラ」。
 * ①解析の aiPersona が無い旧案件も、cast の性格・config 既定文から機能する人格を合成する。
 */
export function buildCompanionPersonasJson({ analysis, startMapId }) {
    const cfg = companionConfig();
    const pair = resolvePlayablePair(analysis);
    if (!cfg || !pair) return null;
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    const title = str(localizedForGeneration(analysis?.game?.title, language, ''));
    const personaFor = (companion, player) => {
        const playerName = localizedForGeneration(player.name, language, player.charId);
        const companionName = localizedForGeneration(companion.name, language, companion.charId);
        const p = companion.cast?.aiPersona ?? {};
        const roleLabel = companion.cast?.role === 'protagonist'
            ? (language === 'en' ? 'the protagonist' : '主人公')
            : (language === 'en' ? 'the partner character' : '相棒キャラクター');
        const persona = {
            role: str(p.role) || (language === 'en'
                ? `${companionName}, ${roleLabel} of ${title ? `"${title}"` : 'this story'}. Now adventuring together with ${playerName}`
                : `${title ? `『${title}』の` : 'この物語の'}${roleLabel}・${companionName}。${playerName}と共に冒険している`),
        };
        for (const [key, fallback] of [
            ['personality', str(companion.cast?.personality)],
            ['speechStyle', ''],
            ['firstPerson', ''],
            ['secondPerson', playerName],
            ['attitude', ''],
            ['goals', ''],
        ]) {
            const value = str(p[key]) || fallback;
            if (value) persona[key] = value;
        }
        const knowledge = asArray(p.knowledge).map((line) => str(line)).filter(Boolean);
        if (knowledge.length > 0) persona.knowledge = knowledge;
        const taboos = asArray(p.taboos).map((line) => str(line)).filter(Boolean);
        const defaultTaboos = asArray(cfg.defaultTaboosByLanguage?.[language]).map((line) => str(line)).filter(Boolean);
        const mergedTaboos = taboos.length > 0 ? taboos : defaultTaboos;
        if (mergedTaboos.length > 0) persona.taboos = mergedTaboos;
        return persona;
    };
    const fallbackLinesFor = (companion) => {
        const own = asArray(companion.cast?.aiPersona?.fallbackLines).map((line) => str(line)).filter(Boolean);
        return own.length > 0
            ? own
            : asArray(cfg.personaFallbackLinesByLanguage?.[language]).map((line) => str(line)).filter(Boolean);
    };
    return {
        formatVersion: '1.0',
        mapId: startMapId,
        _comment: 'NPC AI人格チャット（人格BOT）の基本知識情報。companion（相方プレイアブル）の variants を選択キャラ id で切替える（本家 Sevenfold npc-personas 契約）。fallbackLines / defaultFallbackLines は表示文言のため en.json exact の対訳をツールが自動登録する。persona 本文は LLM 入力専用で対訳不要。',
        defaultFallbackLines: asArray(cfg.defaultFallbackLinesByLanguage?.[language]).map((line) => str(line)).filter(Boolean),
        personas: {
            [str(cfg.npcId) || 'companion']: {
                aiChat: true,
                variants: {
                    [pair.protagonist.charId]: {
                        persona: personaFor(pair.partner, pair.protagonist),
                        fallbackLines: fallbackLinesFor(pair.partner),
                    },
                    [pair.partner.charId]: {
                        persona: personaFor(pair.protagonist, pair.partner),
                        fallbackLines: fallbackLinesFor(pair.protagonist),
                    },
                },
            },
        },
    };
}

/** npc-personas.json をゲームへ書き出す（reapply・⑤-3 取り込みの決定的アーティファクト）。 */
export function writeCompanionPersonas(gameDir, startMapId, analysis) {
    const personas = buildCompanionPersonasJson({ analysis, startMapId });
    if (!personas) return false;
    writeJson(path.join(gameDir, `maps/${startMapId}/npc-personas.json`), personas);
    return true;
}

/**
 * companion（相方 AI チャット NPC）の表示文言（表示名・挨拶・fallbackLines・defaultFallbackLines）を
 * en.json exact へ不足分だけ登録する。config 既定文は ja/en 対の正訳で、①解析由来の aiPersona 文は
 * 対訳が未生成のため原文のまま登録し（identityTexts で報告）、⑤-2 の翻訳パッチ（キー単位マージ）で
 * 正訳へ上書きできるようにする。既存の訳は上書きしない（冪等）。
 */
export function ensureCompanionChatTranslations(gameDir, analysis) {
    const none = { added: 0, identityTexts: [] };
    const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
    if (language === 'en') return none; // 英語生成は日本語原文を持ち込まない
    const cfg = companionConfig();
    const pair = resolvePlayablePair(analysis);
    if (!cfg || !pair) return none;
    const localePath = path.join(gameDir, 'maps/_shared/i18n/locales/en.json');
    const locale = readJsonIfExists(localePath);
    if (!locale || typeof locale !== 'object') return none;
    if (!locale.exact || typeof locale.exact !== 'object') locale.exact = {};
    const exact = locale.exact;
    let added = 0;
    const identityTexts = [];
    const addMissing = (ja, en) => {
        if (typeof ja !== 'string' || ja.trim() === '' || exact[ja] !== undefined) return;
        const value = str(en);
        exact[ja] = value || ja;
        added++;
        if (!value) identityTexts.push(ja);
    };
    const jaNameOf = (member) => localizedForGeneration(member.name, 'ja', member.charId);
    const enNameOf = (member) => localizedForGeneration(member.name, 'en', member.charId);
    // 表示名（dialogueVariants[].name。頭上ラベル・チャットタブで表示される）
    addMissing(jaNameOf(pair.protagonist), enNameOf(pair.protagonist));
    addMissing(jaNameOf(pair.partner), enNameOf(pair.partner));
    // config 既定文（ja/en 対）。挨拶は {name} を両言語の相手名で置換して登録する
    const jaGreetings = asArray(cfg.greetingsByLanguage?.ja);
    const enGreetings = asArray(cfg.greetingsByLanguage?.en);
    for (const player of [pair.protagonist, pair.partner]) {
        jaGreetings.forEach((line, index) => {
            if (typeof line !== 'string') return;
            addMissing(
                line.replaceAll('{name}', jaNameOf(player)),
                str(enGreetings[index] ?? '').replaceAll('{name}', enNameOf(player)),
            );
        });
    }
    for (const key of ['personaFallbackLinesByLanguage', 'defaultFallbackLinesByLanguage']) {
        const jaLines = asArray(cfg[key]?.ja);
        const enLines = asArray(cfg[key]?.en);
        jaLines.forEach((line, index) => {
            if (typeof line !== 'string') return;
            addMissing(line, str(enLines[index] ?? ''));
        });
    }
    // ①解析由来の aiPersona 表示文（greetings / fallbackLines）: 対訳未生成なら原文のまま自己修復
    for (const member of [pair.protagonist, pair.partner]) {
        for (const key of ['greetings', 'fallbackLines']) {
            for (const line of asArray(member.cast?.aiPersona?.[key])) {
                if (typeof line === 'string') addMissing(line, '');
            }
        }
    }
    if (added > 0) writeJson(localePath, locale);
    return { added, identityTexts };
}

/** ⑤-2 翻訳パッチ用: companion の config 既定文（ja/en 対）を事前対訳として返す。 */
function companionConfigTranslationPairs(analysis) {
    const cfg = companionConfig();
    const pair = resolvePlayablePair(analysis);
    if (!cfg || !pair) return [];
    const pairs = [];
    const jaGreetings = asArray(cfg.greetingsByLanguage?.ja);
    const enGreetings = asArray(cfg.greetingsByLanguage?.en);
    for (const member of [pair.protagonist, pair.partner]) {
        const jaName = localizedForGeneration(member.name, 'ja', member.charId);
        const enName = localizedForGeneration(member.name, 'en', member.charId);
        jaGreetings.forEach((line, index) => {
            if (typeof line !== 'string' || typeof enGreetings[index] !== 'string') return;
            pairs.push([line.replaceAll('{name}', jaName), enGreetings[index].replaceAll('{name}', enName)]);
        });
    }
    for (const key of ['personaFallbackLinesByLanguage', 'defaultFallbackLinesByLanguage']) {
        const jaLines = asArray(cfg[key]?.ja);
        const enLines = asArray(cfg[key]?.en);
        jaLines.forEach((line, index) => {
            if (typeof line !== 'string' || typeof enLines[index] !== 'string') return;
            pairs.push([line, enLines[index]]);
        });
    }
    return pairs;
}

/** ⑤-2 翻訳パッチ用: ①解析由来の companion 表示文（npc-personas.json 側の fallbackLines）を集める。 */
function collectCompanionPersonaStrings(analysis) {
    const pair = resolvePlayablePair(analysis);
    if (!pair) return [];
    const out = [];
    for (const member of [pair.protagonist, pair.partner]) {
        for (const line of asArray(member.cast?.aiPersona?.fallbackLines)) {
            if (typeof line === 'string' && line.trim() !== '') out.push(line);
        }
    }
    return out;
}

/** ③のNPCプロフィールから表示身長を決定し、生成済み専用GLBがあれば差し替える。 */
function applyCastNpcModels(json, castModels) {
    if (!castModels) return;
    const cfg = pipelineConfig().worldDesign.engine.placement.castPlacement;
    for (const npc of asArray(json.npcs)) {
        const model = castModels.get(npc?.id);
        if (model?.role !== 'npc') continue;
        npc.targetHeight = npcTargetHeight(model, cfg);
        const spawnFootprint = npcSpawnFootprintFor(npc.targetHeight, cfg);
        if (spawnFootprint) npc.spawnFootprint = spawnFootprint;
        else delete npc.spawnFootprint;
        if (model.hasModel && model.glbPath) {
            npc.modelPath = model.glbPath.startsWith('../') ? model.glbPath : `../${model.glbPath}`;
            // 生成済み通常NPC GLBの前方軸を会話相手へ揃える。
            // JSONで既にゼロ以外の個体別補正があれば、その明示値を優先する。
            npc.behavior = npc.behavior && typeof npc.behavior === 'object' && !Array.isArray(npc.behavior)
                ? { ...npc.behavior }
                : {};
            const currentOffset = Number(npc.behavior.faceYawOffsetDeg);
            if (!Number.isFinite(currentOffset) || currentOffset === 0) {
                npc.behavior.faceYawOffsetDeg = Number(cfg.generatedNpcFaceYawOffsetDeg) || 0;
            }
        }
    }
}

/**
 * v24 より前の通常生成NPCを、実機確認済みの対面角へ移行する。
 * 旧 +45 度と、誤修正 v23 で補正が消えた個体だけを +90 度へ揃え、別角度の明示値は維持する。
 */
function migrateGeneratedNpcFaceYawOffsets(json, castModels) {
    if (!castModels) return 0;
    const cfg = pipelineConfig().worldDesign.engine.placement.castPlacement;
    const desiredOffsetDeg = Number(cfg.generatedNpcFaceYawOffsetDeg);
    const legacyOffsetDeg = Number(cfg.legacyGeneratedNpcFaceYawOffsetDeg);
    if (!Number.isFinite(desiredOffsetDeg) || !Number.isFinite(legacyOffsetDeg)) return 0;
    let updated = 0;
    for (const npc of asArray(json?.npcs)) {
        const model = castModels.get(npc?.id);
        if (model?.role !== 'npc' || !model.hasModel || !model.glbPath) continue;
        const behavior = npc?.behavior;
        const validBehavior = behavior && typeof behavior === 'object' && !Array.isArray(behavior)
            ? behavior
            : null;
        const rawOffset = validBehavior?.faceYawOffsetDeg;
        const currentOffsetDeg = Number(rawOffset);
        const hasFiniteOffset = rawOffset !== undefined && rawOffset !== null && rawOffset !== ''
            && Number.isFinite(currentOffsetDeg);
        if (hasFiniteOffset && currentOffsetDeg === desiredOffsetDeg) continue;
        if (hasFiniteOffset && currentOffsetDeg !== 0 && currentOffsetDeg !== legacyOffsetDeg) continue;
        npc.behavior = { ...(validBehavior || {}), faceYawOffsetDeg: desiredOffsetDeg };
        updated++;
    }
    return updated;
}

/**
 * 生成済み敵マスタへ手続き型モーションの使用有無を明示する。
 * 旧データでは全ての `_move.glb` に sidecar 自動取得が走るため、人型敵へ enabled:false を
 * 補完して不要な404を防ぐ。非人型敵は索引に保存済みの設定をマスタへ埋め込む。
 */
export function applyGeneratedEnemyMotionProfiles(document, promptIndex) {
    if (!Array.isArray(document?.enemies)) return 0;
    const prompts = new Map(asArray(promptIndex?.prompts)
        .filter((entry) => entry?.castId && entry?.role === 'enemy')
        .map((entry) => [entry.castId, entry]));
    let updated = 0;
    for (const enemy of document.enemies) {
        const prompt = prompts.get(enemy?.id);
        if (!prompt) continue;
        const desired = prompt.motionProfile?.enabled === true
            ? prompt.motionProfile
            : prompt.humanoid !== false
                ? { enabled: false }
                : null;
        if (!desired || JSON.stringify(enemy.proceduralMotion) === JSON.stringify(desired)) continue;
        enemy.proceduralMotion = cloneJson(desired);
        updated++;
    }
    return updated;
}

/** 通常敵を基準に、物語上の敵ランク別HP/MP/防御力/ATK倍率を決定的に適用する。 */
export function applyEnemyRankStatMultipliers(stats, rank) {
    const multipliers = pipelineConfig().enemyStatMultipliers ?? {};
    const selected = multipliers[rank] ?? multipliers.normal ?? {};
    const next = cloneJson(stats ?? {});
    for (const field of ['hp', 'mp', 'defense', 'attack']) {
        const base = Number(next[field]);
        const multiplier = Number(selected[field]);
        if (!Number.isFinite(base) || !Number.isFinite(multiplier) || multiplier <= 0) continue;
        next[field] = base * multiplier;
    }
    return next;
}

/**
 * 物語の敵castを、ゲームマスタ（maps/_shared/enemies.json）へ専用タイプとして登録する。
 * ステータス・行動値は⑤LLMが選んだ類似通常敵タイプを複製してランク倍率を適用し、
 * ③の専用GLBがあればモデル・体格も差し替える。再実行しても同cast分を作り直して重複しない。
 */
export function buildCastEnemyTypesJson({ master, analysis, castModels, generatedEnemies }) {
    const cfg = pipelineConfig().worldDesign.engine.placement.castEnemyTypes;
    const document = cloneJson(master ?? { formatVersion: '1.0', enemies: [] });
    document.enemies = asArray(document.enemies);
    const enemyCasts = asArray(analysis?.cast).filter((entry) => entry.role === 'enemy');
    const castIds = new Set(enemyCasts.map((entry) => entry.id));
    // 前回解析にだけ存在した物語敵も marker で除去し、再解析・再生成時に残骸を残さない。
    document.enemies = document.enemies.filter((entry) => entry?.storyGenerated !== true && !castIds.has(entry?.id));
    const referenceDisplayHeight = playerReferenceDisplayHeight(analysis, castModels);
    const placementCfg = pipelineConfig().generatedCastVisualScale ?? {};
    const humanoidRatio = humanoidEnemyHeightRatio(placementCfg);
    let added = 0;
    for (const cast of enemyCasts) {
        const model = castModels?.get(cast.id);
        const baseId = generatedEnemies?.enemyTypes?.[cast.id]?.configId ?? cfg.baseTypeFallback;
        const base = document.enemies.find((entry) => entry.id === baseId)
            ?? document.enemies.find((entry) => entry.id === cfg.baseTypeFallback)
            ?? document.enemies[0];
        if (!base) continue;
        const hasGeneratedModel = !!model?.hasModel && !!model.glbPath;
        const movePath = hasGeneratedModel
            ? (model.glbPath.startsWith('../') ? model.glbPath : `../${model.glbPath}`)
            : null;
        const humanoid = model?.humanoid ?? cast.humanoid ?? true;
        const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
        const enemyRank = cast.enemyRank ?? 'normal';
        const entry = {
            ...cloneJson(base),
            id: cast.id,
            name: localizedForGeneration(cast.name, language, cast.id),
            storyGenerated: true,
            enemyRank,
            stats: applyEnemyRankStatMultipliers(base.stats, enemyRank),
            ...(hasGeneratedModel ? {
                modelPathWalk: movePath,
                // 統合GLB（idle/walk 2クリップ）は同一ファイルを両方へ。旧2ファイル形式の
                // プロジェクト（glbPath が _move.glb のまま）は従来どおり _btl を対にする
                modelPathIdle: movePath.endsWith('_move.glb')
                    ? movePath.replace(/_move\.glb$/, '_btl.glb')
                    : movePath,
                rotationOffsetDeg: 0,
                modelRotationOffsetDeg: cfg.modelRotationOffsetDeg,
            } : {}),
            humanoid,
            ...(humanoid ? { displayHeightRatioToPlayer: humanoidRatio } : {}),
            ...(hasGeneratedModel ? { visual: null } : {}),
            ...(model?.motionProfile?.enabled === true
                ? { proceduralMotion: cloneJson(model.motionProfile) }
                : hasGeneratedModel && model.humanoid !== false
                    ? { proceduralMotion: { enabled: false } }
                    : {}),
        };
        const displayHeight = hasGeneratedModel ? profileDisplayHeight(model.visualProfile, 'enemy', humanoid, {
            playerReferenceDisplayHeight: referenceDisplayHeight,
            placementScale: enemyPlacementDisplayScale(cast.id, generatedEnemies),
        }) : null;
        if (displayHeight != null) {
            entry.displayHeight = displayHeight;
        }
        const colliderHeight = hasGeneratedModel
            ? positiveNumber(model.visualProfile?.game?.colliderHeight) ?? positiveNumber(model.visualProfile?.heightMeters)
            : null;
        const colliderRadius = hasGeneratedModel ? positiveNumber(model.visualProfile?.game?.colliderRadius) : null;
        if (colliderHeight != null || colliderRadius != null) {
            entry.collider = {
                ...cloneJson(base.collider ?? {}),
                ...(colliderHeight != null ? { height: colliderHeight } : {}),
                ...(colliderRadius != null ? { radius: colliderRadius } : {}),
            };
        }
        document.enemies.push(entry);
        added++;
    }
    return { document, added };
}

function applyEnemyHabitats(json, design, gameDir = null) {
    const generated = asArray(json.enemies).filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry));
    const existingEnemyTypes = cloneJson(json.enemyTypes ?? {});
    const enabled = asArray(design?.enemyPlacements).filter((entry) => entry?.enabled !== false);
    if (enabled.length === 0) {
        json.enemyTypes = {};
        json.enemies = [];
        json.totalCount = 0;
        return;
    }
    const placementCfg = pipelineConfig().worldDesign.engine.placement;
    const configuredFallback = placementCfg.castEnemyTypes.baseTypeFallback;
    const placementCastIds = new Set(enabled.map((entry) => entry.castId).filter(Boolean));
    const masterEnemyTypes = gameDir
        ? asArray(readJsonIfExists(path.join(gameDir, 'maps/_shared/enemies.json'))?.enemies)
        : [];
    const allowedBaseTypeIds = new Set(masterEnemyTypes
        .filter((entry) => entry?.id && entry?.storyGenerated !== true && !placementCastIds.has(entry.id))
        .map((entry) => entry.id));
    const hasBaseCatalog = allowedBaseTypeIds.size > 0;
    const fallbackType = hasBaseCatalog
        ? (allowedBaseTypeIds.has(configuredFallback) ? configuredFallback : [...allowedBaseTypeIds][0])
        : configuredFallback;
    const resolveBaseType = (...candidates) => {
        for (const candidate of candidates) {
            if (typeof candidate !== 'string' || candidate === '' || placementCastIds.has(candidate)) continue;
            if (!hasBaseCatalog || allowedBaseTypeIds.has(candidate)) return candidate;
        }
        return fallbackType;
    };
    const placementTotals = new Map();
    for (const placement of enabled) {
        const key = placement.castId || 'enemy';
        placementTotals.set(key, (placementTotals.get(key) ?? 0) + 1);
    }
    const placementOccurrences = new Map();
    const placed = [];
    json.enemyTypes = {};
    for (const [placementIndex, placement] of enabled.entries()) {
        const area = designArea(design, placement.areaId);
        if (!area) continue;
        const source = generated.find((entry) => entry.type === placement.castId
            || entry.id === placement.castId
            || String(entry.id ?? '').startsWith(`${placement.castId}_`))
            ?? generated[placementIndex]
            ?? generated[0]
            ?? { type: resolveBaseType(existingEnemyTypes?.[placement.castId]?.configId), level: 1, behavior: {} };
        // ③で生成済みの敵GLBがある cast は専用タイプへ差し替える。⑤LLMが選んだ既存タイプは
        // enemyTypes[castId].configId として保持し、敵タイプ登録（ステータス複製元）に使う
        const existingCastConfig = existingEnemyTypes?.[placement.castId];
        // 専用GLBの有無にかかわらずcast専用タイプへ統一し、共有敵マスタ側で
        // normal / mid_boss / final_boss の能力倍率を必ず適用できるようにする。
        const castType = placement.castId || null;
        if (castType) {
            const sourceConfigId = resolveBaseType(
                source?.type && source.type !== castType ? source.type : null,
                existingCastConfig?.configId,
            );
            // ③生成GLBはワールド配置単位へ戻した基準 displayHeight をマスタ側に保持する。
            // ここの倍率は通常1.0で固定し、強敵補正は配置単位の displayScale に分離する。
            const numericMultiplier = Number(placementCfg.castEnemyTypes.displayHeightMultiplier);
            json.enemyTypes[castType] = {
                configId: sourceConfigId,
                displayHeightMultiplier: Number.isFinite(numericMultiplier) && numericMultiplier > 0
                    ? numericMultiplier
                    : 1,
            };
        }
        const bounds = placement.spawnBounds ?? area.bounds;
        // 群れの中心を生息域内へ決定的に散布し（常に中央固定だと同一エリアの群れが全部重なる）、
        // メンバーは中心の周囲 activityRadius 内へ広げる。Y は設計地表 +1 のヒント
        // （エンジンが歩行可能地点へ自動スナップするが、水中・高台でも近い高さから探索が始まる）
        const packRadius = clamp(placement.activityRadius, 2, Math.max(2, Math.floor(Math.min(bounds.maxX - bounds.minX, bounds.maxZ - bounds.minZ) / 2)));
        const centerShape = {
            kind: 'rect',
            minX: Math.min(bounds.minX + packRadius, bounds.maxX - 1),
            maxX: Math.max(bounds.maxX - packRadius, bounds.minX + 1),
            minZ: Math.min(bounds.minZ + packRadius, bounds.maxZ - 1),
            maxZ: Math.max(bounds.maxZ - packRadius, bounds.minZ + 1),
        };
        const seedText = `enemy_${placement.castId || placementIndex}_${placementIndex}`;
        const packCenter = scatterPointsInShape({ seedText, count: 1, shape: centerShape })[0]
            ?? { x: Math.floor((bounds.minX + bounds.maxX) / 2), z: Math.floor((bounds.minZ + bounds.maxZ) / 2) };
        const groupSize = Math.max(1, int(placement.groupSize, 1));
        const members = scatterPointsInShape({
            seedText: `${seedText}_members`,
            count: groupSize,
            shape: { kind: 'circle', x: packCenter.x, z: packCenter.z, radius: packRadius },
            minSpacing: placementCfg.enemyPackMemberSpacing,
            accept: (x, z) => x >= bounds.minX && x < bounds.maxX && z >= bounds.minZ && z < bounds.maxZ,
        });
        while (members.length < groupSize) {
            const index = members.length;
            members.push({
                x: clamp(packCenter.x + index, bounds.minX, bounds.maxX - 1),
                z: clamp(packCenter.z + index, bounds.minZ, bounds.maxZ - 1),
            });
        }
        const placementKey = placement.castId || 'enemy';
        const occurrence = (placementOccurrences.get(placementKey) ?? 0) + 1;
        placementOccurrences.set(placementKey, occurrence);
        const idPrefix = (placementTotals.get(placementKey) ?? 0) > 1
            ? `${placementKey}_pack${occurrence}`
            : placementKey;
        for (let index = 0; index < groupSize; index++) {
            const { x, z } = members[index];
            const ground = designGroundInfo(design, x, z);
            const behavior = { ...(source.behavior ?? {}) };
            behavior.type = placement.behavior;
            behavior.radius = placement.activityRadius;
            behavior.wanderRangeBlocks = placement.behavior === 'stationary' ? 0 : placement.activityRadius;
            if (!Number.isFinite(Number(behavior.aggroRadius))) behavior.aggroRadius = Math.max(3, Math.min(placement.activityRadius, 15));
            const enemy = {
                ...cloneJson(source),
                id: `${idPrefix || `enemy_${placementIndex + 1}`}_${String(index + 1).padStart(2, '0')}`,
                ...(castType ? { type: castType } : {}),
                enemyRank: placement.enemyRank,
                displayScale: placement.displayScale,
                pos: [x, (ground?.y ?? design.spawn[1] - 1) + 1, z],
                behavior,
            };
            delete enemy.posOffset;
            delete enemy.posRelativeTo;
            delete enemy.pattern;
            delete enemy.preset;
            placed.push(enemy);
        }
    }
    json.enemies = placed;
    json.totalCount = placed.length;
}

function storyEventActions(event, startMapId, cfg) {
    const messageBase = `events.${startMapId}.${event.id}`;
    if (event.presentation === 'dialogue') {
        const maxLines = Math.max(1, int(cfg.maxDialogueLines, 4));
        const lines = asArray(event.lines).map(str).filter(Boolean).slice(0, maxLines);
        if (lines.length === 0 && str(event.message)) lines.push(str(event.message));
        return [{
            type: 'dialogue',
            speaker: str(event.speaker) || '???',
            lineIds: lines.map((_, index) => `${messageBase}.line${index + 1}`),
            lines,
        }];
    }
    return [{
        type: 'notification',
        messageId: messageBase,
        message: str(event.message) || str(event.summary),
        duration: finite(cfg.notificationDuration, 4),
    }];
}

/** ④で推測した重要地点と表示パターンを、LLMの揺れに依存せずevents.jsonへ確定反映する。 */
export function applyImportantLocationEvents(json, design, startMapId) {
    const ideas = asArray(design?.eventIdeas);
    if (ideas.length === 0) return json;
    const cfg = pipelineConfig().worldDesign?.engine?.importantLocationEvents ?? {};
    const ideaIds = new Set(ideas.map((event) => event.id));
    json.areaEvents = asArray(json.areaEvents).filter((event) => !ideaIds.has(event?.id));
    json.locationEvents = asArray(json.locationEvents).filter((event) => !ideaIds.has(event?.id));
    for (const event of ideas) {
        const actions = storyEventActions(event, startMapId, cfg);
        if (event.trigger === 'proximity' && asArray(event.position).length >= 3) {
            const x = int(event.position[0], 0);
            const z = int(event.position[2], 0);
            const ground = designGroundInfo(design, x, z);
            const y = Number.isFinite(Number(ground?.y)) ? Number(ground.y) + 1 : int(event.position[1], 0);
            json.locationEvents.push({
                id: event.id,
                trigger: 'proximity',
                pos: [x, y, z],
                radius: clamp(
                    finite(event.radius, finite(cfg.defaultRadius, 12)),
                    finite(cfg.minRadius, 2),
                    finite(cfg.maxRadius, 40),
                ),
                verticalRadius: finite(cfg.verticalRadius, 8),
                once: event.once !== false,
                actions,
            });
        } else {
            json.areaEvents.push({
                id: event.id,
                trigger: 'area_enter',
                areaId: event.areaId,
                once: event.once !== false,
                actions,
            });
        }
    }
    json.interactionZones = asArray(json.interactionZones);
    json.timerEvents = asArray(json.timerEvents);
    json.questEvents = asArray(json.questEvents);
    return json;
}

/**
 * 生成 JSON へ④の確定設計を決定的に反映する。
 * LLM が後段で中心小マップ・外周荒野・空配列へ戻しても、縮尺・地面・自然・生活圏・生息地は失われない。
 */
export function applyWorldDesignToUnit(unitId, input, { startMapId, design, existingTarget = null, gameDir = null, analysis = null, castModels = null }) {
    let json = input;
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
        throw new Error('出力が JSON オブジェクトではありません');
    }
    if (unitId === 'environment' && existingTarget && typeof existingTarget === 'object') {
        const merged = cloneJson(existingTarget);
        deepMergeInto(merged, json);
        json = merged;
    }
    if (typeof json.formatVersion !== 'string') json.formatVersion = '1.0';
    if (unitId === 'meta') json.id = startMapId;
    else if (unitId !== 'items' && unitId !== 'skills') json.mapId = startMapId;
    if (unitId === 'items') json = normalizeStoryItems(json, existingTarget, analysis);
    if (unitId === 'skills') json = normalizeStorySkills(json, existingTarget, analysis);
    if (design && unitId === 'meta') {
        const engine = pipelineConfig().worldDesign.engine;
        const language = analysis?.game?.generationLanguage === 'en' ? 'en' : 'ja';
        const areaName = (area) => language === 'en'
            ? (str(area.nameEn) || str(area.nameJa))
            : (str(area.nameJa) || str(area.nameEn));
        json.world ??= {};
        json.world.size = { width: design.scale.worldSize.width, depth: design.scale.worldSize.depth };
        json.world.chunkSize = engine.world.chunkSize;
        json.player ??= {};
        json.player.spawnPos = cloneJson(design.spawn);
        json.player.respawnPos = cloneJson(design.spawn);
        // spawnPos[1] は目安。エンジンの自動接地（getGroundHeight）を常に有効にする
        delete json.player.useSpawnPosY;
        json.areas = design.areas.map((area) => ({
            id: area.id,
            name: areaName(area),
            type: area.type,
            shape: 'rect',
            bounds: cloneJson(area.bounds),
            surfaceBlock: area.surfaceBlock,
            priority: area.priority,
        }));
        const spawnArea = [...design.areas]
            .sort((a, b) => b.priority - a.priority)
            .find((area) => design.spawn[0] >= area.bounds.minX && design.spawn[0] < area.bounds.maxX
                && design.spawn[2] >= area.bounds.minZ && design.spawn[2] < area.bounds.maxZ);
        if (spawnArea) json.defaultAreaName = areaName(spawnArea);
        json.hasShops = true;
    }
    if (design && unitId === 'terrain') {
        const engine = pipelineConfig().worldDesign.engine;
        json.generator = design.foundation.generator;
        json.worldCenter = cloneJson(engine.world.center);
        json.elevation = cloneJson(design.terrain.elevation);
        json.boundaries = cloneJson(design.terrain.boundaries);
        // stairs は常に明示する（キー欠落だとエンジンが既定位置へフォールバック階段を作る。[] は「階段なし」の明示）
        json.stairs = cloneJson(design.terrain.stairs);
        json.noise = cloneJson(design.terrain.noise);
        json.water = cloneJson(design.terrain.water);
        // range 付き道路と void の道路は地表追従の敷石 blockFill として objects 側へ変換される
        json.roads = design.foundation.generator === 'overworld'
            ? design.terrain.roads.filter((road) => !road.range).map((road) => cloneJson(road))
            : [];
        json.vegetation = cloneJson(design.terrain.vegetation);
        // 城（landmarks）と都市外周壁（cityWall）はエンジンの基準座標・壁生成の参照元
        const castleLandmark = asArray(design.landmarks).find((landmark) => landmark.kind === 'castle');
        json.castle = castleLandmark
            ? { enabled: true, positionZ: castleLandmark.positionZ }
            : { ...(json.castle && typeof json.castle === 'object' ? json.castle : {}), enabled: false };
        json.walls = {
            perimeter: design.cityWall?.enabled
                ? {
                    enabled: true,
                    position: design.cityWall.position,
                    height: design.cityWall.height,
                    thickness: design.cityWall.thickness,
                    towerBaseSize: design.cityWall.towerBaseSize,
                    towerHeight: design.cityWall.towerHeight,
                    wallBlockType: design.cityWall.wallBlockType,
                    towerBlockType: design.cityWall.towerBlockType,
                    roofBlockType: design.cityWall.roofBlockType,
                    groundBlockType: design.cityWall.groundBlockType,
                    towerGroundBlockType: design.cityWall.towerGroundBlockType,
                }
                : { enabled: false },
        };
    }
    if (design && unitId === 'environment') {
        json.timeOfDay = cloneJson(design.environment.timeOfDay);
        json.weather = cloneJson(design.environment.weather);
    }
    if (design && unitId === 'objects') {
        const designFills = [...design.foundation.fills, ...design.detailFills];
        const generatedFills = buildGeneratedFills(design);
        const landmarkArtifacts = buildLandmarkArtifacts(design, { gameDir });
        const usedIds = new Set([...designFills, ...generatedFills, ...landmarkArtifacts.fills].map((fill) => fill.id));
        // LLM の小さな装飾 fill（看板・街灯・樽など）は選別して残し、木立の樹木は最後に重ねる
        const llmExtraFills = collectLlmExtraFills(json, design, usedIds);
        const groveFills = buildGroveFills(design);
        json.blockFills = [
            ...designFills.map(toEngineBlockFill),
            ...generatedFills.map(toEngineBlockFill),
            ...landmarkArtifacts.fills.map(toEngineBlockFill),
            ...llmExtraFills,
            ...groveFills.map(toEngineBlockFill),
        ];
        // 城オブジェクトはツールが一元管理する（LLM 由来の castle は除去して二重生成を防ぐ）
        json.objects = [
            ...asArray(json.objects).filter((entry) => entry?.type !== 'castle'),
            ...landmarkArtifacts.objects,
        ];
        // 都市外周壁と外門（設計 cityWall から確定反映。labyrinthBox は初期ワールドでは未使用）
        const engine = pipelineConfig().worldDesign.engine;
        const castleLandmark = asArray(design.landmarks).find((landmark) => landmark.kind === 'castle');
        const gateDefaults = engine.cityWall.gateDefaults;
        json.structures = {
            perimeterWalls: { enabled: design.cityWall?.enabled === true },
            outerGate: design.cityWall?.enabled && design.cityWall.gate.enabled
                ? {
                    enabled: true,
                    posRelativeTo: 'castle',
                    offset: [0, 0, engine.world.center.z + design.cityWall.position - (castleLandmark?.positionZ ?? engine.world.center.z)],
                    gate: { height: gateDefaults.gateHeight },
                    walls: { extensionLength: gateDefaults.extensionLength, height: gateDefaults.wallHeight, thickness: gateDefaults.wallThickness },
                    towers: { height: gateDefaults.towerHeight, radius: gateDefaults.towerRadius, hasRoof: true },
                    blockType: gateDefaults.blockType,
                    battlements: true,
                }
                : { enabled: false },
            labyrinthBox: [],
        };
        expandDesignedBuildings(json, design);
    }
    if (unitId === 'enemies' && !design) json.enemyTypes = {}; // 設計ありは applyEnemyHabitats が安全なconfigId参照だけへ再構成する
    if (unitId === 'npcs') {
        for (const npc of asArray(json.npcs)) {
            if (npc && typeof npc === 'object' && 'scale' in npc) {
                // 共通静的モデル × scale はモデル極小化の既知不具合（計画書 §14）。targetHeight へ置換する
                delete npc.scale;
                if (!Number.isFinite(npc.targetHeight)) npc.targetHeight = 1.7;
            }
        }
        if (design) {
            backfillDesignedNpcs(json, design, analysis, castModels); // 解析済み npc cast は必ず配置する
            applyCastNpcModels(json, castModels);                     // ③の専用GLBがあれば共通モデルから差し替え
            applyNpcHabitats(json, design);
            upsertCompanionNpc(json, { analysis, design, gameDir });  // 相方プレイアブルの AI チャット NPC をスポーン付近へ配置
        }
    }
    if (unitId === 'enemies' && design) applyEnemyHabitats(json, design, gameDir);
    if (unitId === 'events' && design) applyImportantLocationEvents(json, design, startMapId);
    for (const listKey of ['objects', 'npcs', 'enemies']) {
        if (Array.isArray(json[listKey]) && 'totalCount' in json) json.totalCount = json[listKey].length;
    }
    return json;
}

/**
 * 保存済みのワールド設計を、プレビューで使用中の設計依存ファイルへ決定的に再反映する。
 * 旧formatVersionの自動移行時と⑤-3取り込み直後の双方で使い、古い生成JSONによる巻き戻りを防ぐ。
 */
export function reapplyWorldDesignArtifacts(projectId, gameId = null) {
    const meta = readProjectMeta(projectId);
    const resolvedGameId = gameId ?? meta.gameId;
    if (!resolvedGameId) return { updated: 0, units: [] };
    const analysis = requireAnalysis(projectId);
    const design = readNormalizedWorldDesign(projectId, analysis);
    if (!design) return { updated: 0, units: [] };
    const dir = requireGameDir(resolvedGameId);
    const startMapId = analysis.game.startMapId;
    const castModels = castModelsFromPromptIndex(readJsonIfExists(projectPaths(projectId).imagePromptsIndex));
    const previousArtifactsVersion = Number(meta.worldArtifactsVersion);
    const shouldMigrateGeneratedNpcYaw = !Number.isFinite(previousArtifactsVersion)
        || previousArtifactsVersion < GENERATED_NPC_YAW_MIGRATION_VERSION;
    const units = [];
    const charactersPath = path.join(dir, 'maps/_shared/characters.json');
    const characters = readJsonIfExists(charactersPath);
    if (characters && ensureEssentialCharacterInitialItems(characters) > 0) {
        writeJson(charactersPath, characters);
        units.push('characters-initial-items');
    }
    // プレイアブルの HP バー位置（実身長+マージンのワールド固定オフセット）を characters.json へ反映。
    // 後続の npcs ユニット再適用（companion 生成）が同じ値を参照するため、必ず unit ループより先に行う。
    if (ensurePlayableHeadLabelWorldY(dir) > 0) units.push('characters-head-label');
    for (const unitId of ['meta', 'terrain', 'objects', 'npcs', 'enemies', 'events']) {
        const target = path.join(dir, `maps/${startMapId}/${unitId}.json`);
        const current = readJsonIfExists(target);
        if (!current) continue;
        if (unitId === 'npcs'
            && shouldMigrateGeneratedNpcYaw
            && migrateGeneratedNpcFaceYawOffsets(current, castModels) > 0) {
            units.push('npc-face-yaw');
        }
        const patched = applyWorldDesignToUnit(unitId, current, {
            startMapId,
            design,
            existingTarget: current,
            gameDir: dir,
            analysis,
            castModels,
        });
        writeJson(target, patched);
        units.push(unitId);
    }
    // 旧案件のプレビューだけを直して generated/enemies.json を空のまま残すと、⑤-2の
    // 編集画面・次回⑤-3の取り込み元が再び敵0体を示す。保存済み生成物も同じ設計で直し、
    // import.txt を組み直して「表示上だけ直った」二重状態を残さない。
    let generatedArtifactsChanged = false;
    const generatedNpcsPath = path.join(projectPaths(projectId).generatedDir, 'npcs.json');
    const storedGeneratedNpcs = readJsonIfExists(generatedNpcsPath);
    if (storedGeneratedNpcs
        && shouldMigrateGeneratedNpcYaw
        && migrateGeneratedNpcFaceYawOffsets(storedGeneratedNpcs, castModels) > 0) {
        const patchedGeneratedNpcs = applyWorldDesignToUnit('npcs', storedGeneratedNpcs, {
            startMapId,
            design,
            existingTarget: readJsonIfExists(path.join(dir, `maps/${startMapId}/npcs.json`)),
            gameDir: dir,
            analysis,
            castModels,
        });
        writeJson(generatedNpcsPath, patchedGeneratedNpcs);
        generatedArtifactsChanged = true;
        units.push('generated-npcs');
    }
    const generatedEnemiesPath = path.join(projectPaths(projectId).generatedDir, 'enemies.json');
    const storedGeneratedEnemies = readJsonIfExists(generatedEnemiesPath);
    if (storedGeneratedEnemies) {
        const patchedGeneratedEnemies = applyWorldDesignToUnit('enemies', storedGeneratedEnemies, {
            startMapId,
            design,
            existingTarget: readJsonIfExists(path.join(dir, `maps/${startMapId}/enemies.json`)),
            gameDir: dir,
            analysis,
            castModels,
        });
        writeJson(generatedEnemiesPath, patchedGeneratedEnemies);
        generatedArtifactsChanged = true;
        units.push('generated-enemies');
    }
    // 補完NPCの名前・既定会話や注入イベント通知文は⑤-2当時のi18nパッチに含まれない場合が
    // あるため、en.json exact の不足分をここで決定的に補う（validate-i18n の未翻訳失敗を防ぐ）。
    if (ensureBackfillNpcExactTranslations(dir, design, analysis) > 0) units.push('npc-i18n');
    const eventI18n = ensureImportantLocationEventTranslations(dir, design, analysis);
    if (eventI18n.added > 0) units.push('event-i18n');
    // 相方プレイアブルの AI チャット NPC 用ペルソナ（npc-personas.json）と表示文言の対訳を決定的に生成する。
    if (writeCompanionPersonas(dir, startMapId, analysis)) units.push('npc-personas');
    const companionI18n = ensureCompanionChatTranslations(dir, analysis);
    if (companionI18n.added > 0) units.push('companion-i18n');
    // 保存済み案件の生成敵マスタも、配置倍率を含む現行の人型身長制約で再構成する。
    // LLM・画像・GLBの有料再生成は行わず、保存済みプロフィールと配置JSONのみを使う。
    const enemyMasterPath = path.join(dir, 'maps/_shared/enemies.json');
    const generatedEnemies = readJsonIfExists(path.join(dir, `maps/${startMapId}/enemies.json`));
    const enemyMaster = readJsonIfExists(enemyMasterPath);
    if (enemyMaster && generatedEnemies) {
        const rebuilt = buildCastEnemyTypesJson({
            master: enemyMaster,
            analysis,
            castModels,
            generatedEnemies,
        });
        writeJson(enemyMasterPath, rebuilt.document);
        const generatedEnemyTypesPath = path.join(projectPaths(projectId).generatedDir, 'enemy-types.json');
        if (existsSync(generatedEnemyTypesPath)) {
            writeJson(generatedEnemyTypesPath, rebuilt.document);
            generatedArtifactsChanged = true;
            units.push('generated-enemy-types');
        }
        units.push('enemy-types');
    }
    if (generatedArtifactsChanged) rebuildImportText(projectId, startMapId);
    const itemsPath = path.join(dir, 'maps/_shared/items.json');
    const shopMasterPath = path.join(dir, 'maps/_shared/shop-master.json');
    const shopsPath = path.join(dir, `maps/${startMapId}/shops.json`);
    const items = readJsonIfExists(itemsPath);
    const shopMaster = readJsonIfExists(shopMasterPath);
    const shops = readJsonIfExists(shopsPath) ?? {
        formatVersion: '1.0',
        mapId: startMapId,
        shopProfiles: [],
    };
    const shopResult = sanitizeShopSales({
        items,
        shopMaster,
        shops,
        allowedMapIds: availableGameMapIds(dir, startMapId),
    });
    if (shopResult.removed.length > 0 && shopMaster) writeJson(shopMasterPath, shopMaster);
    const hasStoryItems = asArray(items?.items).some((item) => item?.storyGenerated === true);
    // 初期弾薬は「ショップで買える弾」に限るため、書き込み後の実際の販売内容を保持しておく
    let effectiveShops = shops;
    if (hasStoryItems) {
        effectiveShops = buildStoryShopJson({ items, startMapId });
        writeJson(shopsPath, effectiveShops);
        units.push('shops');
    } else if (ensureEssentialShopAvailability(shops, startMapId) > 0) {
        writeJson(shopsPath, shops);
        units.push('shops');
    } else if (shopResult.removed.length > 0) {
        if (shops) writeJson(shopsPath, shops);
        units.push('shops');
    }
    // 物語武器: items.json の weapons[] を equipment.json（weapon スロット）へマージし、
    // 使い手（owner）の初期装備・初期弾薬を保証する（docs/攻撃方法の実装案.md §6.5）
    const storyWeapons = asArray(items?.weapons).filter((weapon) => weapon?.storyGenerated === true);
    const equipmentPath = path.join(dir, 'maps/_shared/equipment.json');
    const equipmentDoc = readJsonIfExists(equipmentPath);
    if (equipmentDoc && applyStoryWeaponsToEquipment(equipmentDoc, storyWeapons) > 0) {
        writeJson(equipmentPath, equipmentDoc);
        units.push('story-weapons');
    }
    const charactersForWeapons = readJsonIfExists(charactersPath);
    if (charactersForWeapons && ensureStoryWeaponLoadout(charactersForWeapons, storyWeapons, items, {
        shops: effectiveShops, shopMaster,
    }) > 0) {
        writeJson(charactersPath, charactersForWeapons);
        units.push('story-weapon-loadout');
    }
    // アイテム一覧の「敵用」カテゴリが武器系効果を含むことを保証（マスタ更新の既存ゲームへの同期）
    const itemCategoriesPath = path.join(dir, 'maps/_shared/item-categories.json');
    const itemCategoriesDoc = readJsonIfExists(itemCategoriesPath);
    if (itemCategoriesDoc && ensureOffenseCategoryCoversWeaponEffects(itemCategoriesDoc) > 0) {
        writeJson(itemCategoriesPath, itemCategoriesDoc);
        units.push('item-categories-offense');
    }
    // 武器の nameKey / descriptionKey のロケールキーを欠落分だけ原文で保証する。
    // ⑥-2 の i18n パッチ再生成前の取り込みでも F5 i18n 検証が通るようにする（正訳はパッチが上書き）
    if (storyWeapons.length > 0) {
        for (const lang of ['ja', 'en']) {
            const localePath = path.join(dir, `maps/_shared/i18n/locales/${lang}.json`);
            const locale = readJsonIfExists(localePath);
            if (locale && applyStoryWeaponTranslationBackfill(locale, storyWeapons) > 0) {
                writeJson(localePath, locale);
                units.push(`story-weapon-i18n-${lang}`);
            }
        }
    }
    updateProjectMeta(projectId, (current) => ({ ...current, worldArtifactsVersion: WORLD_ARTIFACTS_VERSION }));
    return {
        updated: units.length,
        units,
        formatVersion: design.formatVersion,
        artifactsVersion: WORLD_ARTIFACTS_VERSION,
        removedForbiddenShopEntries: shopResult.removed.length,
        i18nIdentityTexts: [...eventI18n.identityTexts, ...companionI18n.identityTexts],
    };
}

async function generateUnit({ unit, target, requestMd, analysis, design, catalogs, gameId, gameDir, projectId, startMapId, generatedMeta, visualProfiles, motionProfiles, castModels, onLog }) {
    const cfg = pipelineConfig().generate;
    const schemaPath = schemaFileFor(gameDir, target);
    const schemaText = schemaPath ? readFileSync(schemaPath, 'utf-8') : '（対応スキーマなし）';
    const exampleText = readFileSync(path.join(TOOLS_DIR, unit.exampleFile), 'utf-8');
    const existingTarget = readJsonIfExists(path.join(gameDir, target));
    const vars = {
        startMapId,
        generationLanguageName: generationLanguageName(analysis.game?.generationLanguage),
    };
    const prompt = [
        requestMd,
        '',
        '─── ここまでが依頼文。以下、今回の生成対象と生成ルール ───',
        '',
        `今回の出力対象: ${target}（このファイル 1つ分の JSON オブジェクトのみを出力する）`,
        '',
        '## 対象スキーマ（JSON Schema。必ず適合させる）',
        schemaText,
        '',
        '## 検証済みの実例（サンプルクエストの同種ファイル。構造とエンジン調整値の基準。内容はこの作品に合わせて変える）',
        exampleText,
        '',
        buildUnitContext({ unit, analysis, design, catalogs, generatedMeta, visualProfiles, motionProfiles }),
        '## このファイル固有の生成ルール',
        ...unit.instructions.map((line) => `- ${fillTemplate(line, vars)}`),
        '',
        '## 共通ルール',
        ...cfg.commonRules.map((line) => `- ${fillTemplate(line, vars)}`),
        '',
        `出力: ${target} の完全な JSON オブジェクト 1個のみ。コードフェンス・説明文・コメント禁止。`,
    ].join('\n');

    let r = await callLlmJson('generateJson', { system: cfg.system, prompt, onLog });
    addUsage(projectId, r.usage, r.calls);
    let json = applyWorldDesignToUnit(unit.id, r.json, { startMapId, design, existingTarget, gameDir, analysis, castModels });
    let schema = validateSingleJson(gameId, target, json);
    if (!schema.ok && !schema.skipped) {
        // スキーマ NG: 検証エラーを添えて 1 回だけ再生成させる
        const errors = schema.errors.slice(0, 10).join('\n');
        onLog(`  スキーマ NG（${schema.errors.length}件）→ エラーを添えて再生成します`);
        const retryMessages = [
            { role: 'system', content: cfg.system },
            { role: 'user', content: prompt },
            { role: 'assistant', content: JSON.stringify(json).slice(0, 12000) },
            { role: 'user', content: `${cfg.retryNote}\n${errors}` },
        ];
        const second = await callLlm('generateJson', { messages: retryMessages, onLog });
        addUsage(projectId, second.usage, 1);
        json = applyWorldDesignToUnit(unit.id, parseJsonLoose(second.text), { startMapId, design, existingTarget, gameDir, analysis, castModels });
        schema = validateSingleJson(gameId, target, json);
        r = { ...r, usage: mergeUsage(r.usage, second.usage), model: second.model };
        if (!schema.ok && !schema.skipped) {
            throw new Error(`スキーマ検証 NG（再生成後も ${schema.errors.length}件）: ${schema.errors.slice(0, 5).join(' / ')}`);
        }
    }
    return { json, model: r.model, usage: r.usage, schema };
}

/** _shared/game-info.json の novelInfo だけを analysis から差し替える（weapons 等は既存維持のコード生成ユニット）。 */
function buildGameInfoJson(gameDir, analysis) {
    const current = readJsonIfExists(path.join(gameDir, 'maps/_shared/game-info.json')) ?? { formatVersion: '1.0' };
    const language = analysis.game.generationLanguage === 'en' ? 'en' : 'ja';
    const primaryTitle = localizedForGeneration(analysis.game.title, language, analysis.game.gameId);
    return {
        ...current,
        novelInfo: {
            ...(current.novelInfo ?? {}),
            title: primaryTitle,
            subtitle: normalizeStorySubtitle(analysis.game.subtitle, localizedForGeneration(analysis.game.description, language, primaryTitle)),
            englishTitle: analysis.game.title.en,
            originalUrl: analysis.game.originalUrl ?? null,
            sourceLanguage: analysis.game.sourceLanguage ?? language,
            generationLanguage: language,
            genre: analysis.game.genre,
            worldSetting: analysis.game.worldSetting || analysis.world.startMap.concept,
            mainColor: analysis.game.colors.main,
            accentColor: analysis.game.colors.accent,
            goldColor: analysis.game.colors.gold,
        },
    };
}

/** 生成済みユニット JSON から生文字列（マップ名・エリア名・NPC 名・会話・通知文）を抽出する。 */
function collectRawStrings(generatedDir) {
    const hasJapanese = (s) => /[　-ヿ㐀-鿿豈-﫿！-｠]/.test(s);
    const out = new Set();
    const push = (v) => {
        if (typeof v === 'string' && v.trim() !== '' && hasJapanese(v)) out.add(v);
    };
    const meta = readJsonIfExists(path.join(generatedDir, 'meta.json'));
    if (meta) {
        push(meta.name);
        push(meta.description);
        push(meta.defaultAreaName);
        for (const a of asArray(meta.areas)) push(a?.name);
    }
    const npcs = readJsonIfExists(path.join(generatedDir, 'npcs.json'));
    if (npcs) {
        for (const npc of asArray(npcs.npcs)) {
            push(npc?.name);
            for (const d of asArray(npc?.dialogues)) push(d);
            // companion（相方 AI チャット NPC）の variant 表示名・挨拶も表示文言として英訳対象にする
            for (const variant of Object.values(npc?.dialogueVariants ?? {})) {
                push(variant?.name);
                for (const d of asArray(variant?.dialogues)) push(d);
            }
        }
    }
    for (const name of ['events.json', 'objects.json']) {
        const json = readJsonIfExists(path.join(generatedDir, name));
        if (!json) continue;
        walkJson(json, ({ key, value }) => {
            if (['message', 'prompt', 'destinationName', 'label'].includes(key)) push(value);
        });
    }
    return [...out];
}

/** events.json の messageId ⇔ message から locales の messages パッチを組み立てる。 */
function collectEventMessages(generatedDir) {
    const events = readJsonIfExists(path.join(generatedDir, 'events.json'));
    const pairs = [];
    if (!events) return pairs;
    walkJson(events, ({ key, value, path: p }) => {
        if (key !== 'messageId' || typeof value !== 'string' || !/^[a-zA-Z0-9_.]+$/.test(value)) return;
        pairs.push({ messageId: value, jsonPath: p });
    });
    // messageId と同じ action オブジェクト内の message を対応づける（構造走査で拾い直す）
    const byId = new Map(pairs.map((x) => [x.messageId, null]));
    const dialoguePairs = [];
    const visit = (node) => {
        if (Array.isArray(node)) return node.forEach(visit);
        if (!node || typeof node !== 'object') return;
        if (typeof node.messageId === 'string' && typeof node.message === 'string' && byId.has(node.messageId)) {
            byId.set(node.messageId, node.message);
        }
        if (Array.isArray(node.lineIds) && Array.isArray(node.lines)) {
            for (let index = 0; index < Math.min(node.lineIds.length, node.lines.length); index++) {
                const messageId = node.lineIds[index];
                const message = node.lines[index];
                if (typeof messageId === 'string' && typeof message === 'string') dialoguePairs.push({ messageId, message });
            }
        }
        Object.values(node).forEach(visit);
    };
    visit(events);
    return [
        ...[...byId.entries()].filter(([, msg]) => msg).map(([messageId, message]) => ({ messageId, message })),
        ...dialoguePairs,
    ];
}

/** 物語固有アイテム／スキルの表示キーと日本語原文を集める。 */
function collectStoryContentMessages(generatedDir) {
    const pairs = [];
    const items = readJsonIfExists(path.join(generatedDir, 'items.json'));
    for (const item of asArray(items?.items)) {
        if (item?.storyGenerated !== true) continue;
        if (typeof item.nameKey === 'string' && typeof item.name === 'string') {
            pairs.push({ messageId: item.nameKey, message: item.name });
        }
        if (typeof item.descriptionKey === 'string' && typeof item.description === 'string') {
            pairs.push({ messageId: item.descriptionKey, message: item.description });
        }
    }
    // 物語武器（items.json の weapons[]。取り込み時に equipment.json へマージされる）の
    // nameKey / descriptionKey も同じ翻訳パッチへ載せる。漏れると F5 i18n 検証の
    // 「マスタ由来キー欠落（equipment.json）」で取り込みが失敗する
    for (const weapon of asArray(items?.weapons)) {
        if (weapon?.storyGenerated !== true) continue;
        if (typeof weapon.nameKey === 'string' && typeof weapon.name === 'string') {
            pairs.push({ messageId: weapon.nameKey, message: weapon.name });
        }
        if (typeof weapon.descriptionKey === 'string' && typeof weapon.description === 'string') {
            pairs.push({ messageId: weapon.descriptionKey, message: weapon.description });
        }
    }
    const skills = readJsonIfExists(path.join(generatedDir, 'skills.json'));
    for (const skill of asArray(skills?.activeSkills)) {
        if (skill?.storyGenerated !== true || typeof skill.id !== 'string') continue;
        if (typeof skill.name === 'string') {
            pairs.push({ messageId: `skills.active.${skill.id}.name`, message: skill.name });
        }
        if (typeof skill.description === 'string') {
            pairs.push({ messageId: `skills.active.${skill.id}.description`, message: skill.description });
        }
    }
    return pairs;
}

/** i18n パッチ（ja/en）を生成する。日本語原文のみ OpenRouter で英訳する。 */
async function buildI18nPatches({ projectId, analysis, generatedDir, onLog }) {
    const cfg = pipelineConfig().translate;
    const rawStrings = collectRawStrings(generatedDir);
    // npc-personas.json 側の表示文言（aiPersona 由来 fallbackLines）は generated ディレクトリに
    // 実体ファイルを持たないため、解析結果から直接収集して同じ翻訳パッチへ載せる。
    for (const line of collectCompanionPersonaStrings(analysis)) {
        if (!rawStrings.includes(line)) rawStrings.push(line);
    }
    const eventMessages = collectEventMessages(generatedDir);
    const storyMessages = collectStoryContentMessages(generatedDir);
    const keyedMessages = [...eventMessages, ...storyMessages];
    if (rawStrings.length === 0 && keyedMessages.length === 0) {
        return { rawCount: 0, eventCount: 0, storyContentCount: 0 };
    }

    const glossary = [
        `作品タイトル: ${analysis.game.title.ja} = ${analysis.game.title.en}`,
        `開始ワールド: ${analysis.world.startMap.name.ja} = ${analysis.world.startMap.name.en}`,
        ...analysis.cast.map((c) => `${c.name.ja} = ${c.name.en}`),
    ].join('\n');
    const targets = [...new Set([...rawStrings, ...keyedMessages.map((e) => e.message)])];
    const translations = {};
    const generationLanguage = analysis.game?.generationLanguage === 'en' ? 'en' : 'ja';
    if (generationLanguage === 'en') {
        // 英語（および日本語以外）の入力は生成済み文言を原文とし、
        // 日本語→英語翻訳器に通して書き換えたり追加課金したりしない。
        for (const target of targets) translations[target] = target;
        onLog(`i18n: 英語原文 ${targets.length}件を両ロケールにそのまま登録します`);
    } else {
        // companion の config 既定文は ja/en 対で保持しているため、LLM 翻訳せず正訳を直接使う
        for (const [ja, en] of companionConfigTranslationPairs(analysis)) {
            if (typeof ja === 'string' && ja && typeof en === 'string' && en && translations[ja] === undefined) {
                translations[ja] = en;
            }
        }
        const pending = targets.filter((target) => translations[target] === undefined);
        onLog(`i18n: 生文字列 ${pending.length}件を英訳します`);
        const batchSize = 40;
        for (let i = 0; i < pending.length; i += batchSize) {
            const batch = pending.slice(i, i + batchSize);
            const r = await callLlmJson('translate', {
                system: cfg.system,
                prompt: fillTemplate(cfg.prompt, { glossary, stringsJson: JSON.stringify(batch, null, 1) }),
                onLog,
            });
            addUsage(projectId, r.usage, r.calls);
            for (const [ja, en] of Object.entries(r.json?.translations ?? {})) {
                if (typeof en === 'string' && en.trim() !== '') translations[ja] = en.trim();
            }
        }
    }
    const untranslated = targets.filter((t) => !translations[t]);
    if (untranslated.length > 0) onLog(`  ⚠ 未翻訳 ${untranslated.length}件（原文のまま登録。後で en.json exact を手修正）`);

    // messages.events.<startMapId>.<key> パッチ（ja は原文、en は訳文）。
    // LLM 由来の messageId は「a.b」と「a.b.c」のような接頭辞衝突があり得る。
    // 衝突は該当 ID だけスキップして警告し、パッチ全体を TypeError で失敗させない
    const setByMessageId = (patch, messageId, text) => {
        const segs = messageId.split('.');
        let cur = patch.messages;
        for (const seg of segs.slice(0, -1)) {
            if (cur[seg] !== undefined && (typeof cur[seg] !== 'object' || cur[seg] === null || Array.isArray(cur[seg]))) {
                onLog(`  ⚠ messageId「${messageId}」は既存キー「${seg}」（文字列）と衝突するためスキップしました`);
                return;
            }
            cur = cur[seg] ??= {};
        }
        cur[segs[segs.length - 1]] = text;
    };
    const jaPatch = { _comment: '物語ドキュメント駆動モードが生成した i18n パッチ（ja）。locales へキー単位マージされる', messages: {} };
    const enPatch = { _comment: '物語ドキュメント駆動モードが生成した i18n パッチ（en）。locales へキー単位マージされる', messages: {}, exact: {} };
    for (const { messageId, message } of keyedMessages) {
        setByMessageId(jaPatch, messageId, message);
        setByMessageId(enPatch, messageId, translations[message] ?? message);
    }
    for (const ja of rawStrings) enPatch.exact[ja] = translations[ja] ?? ja;

    const patchDir = path.join(generatedDir, 'i18n-patch');
    mkdirSync(patchDir, { recursive: true });
    writeJson(path.join(patchDir, 'ja.patch.json'), jaPatch);
    writeJson(path.join(patchDir, 'en.patch.json'), enPatch);
    return {
        rawCount: rawStrings.length,
        eventCount: eventMessages.length,
        storyContentCount: storyMessages.length,
        untranslated: untranslated.length,
    };
}

/** 生成済みファイルから F2 取り込み形式（パス + JSON コードブロック）の import.txt を組み立てる。 */
function rebuildImportText(projectId, startMapId) {
    const p = projectPaths(projectId);
    const lines = ['# 物語ドキュメント駆動モードの生成結果（F2 取り込み形式。取り込みタブ / story import で配置）', ''];
    const entries = [
        ...pipelineConfig().generate.units.map((u) => ({ file: `${u.id}.json`, target: u.target.replaceAll('{startMapId}', startMapId) })),
        ...CODE_UNITS.map((u) => ({ file: `${u.id}.json`, target: u.target.replaceAll('{startMapId}', startMapId) })),
    ];
    let count = 0;
    for (const { file, target } of entries) {
        const abs = path.join(p.generatedDir, file);
        if (!existsSync(abs)) continue;
        lines.push(target, '```json', readFileSync(abs, 'utf-8').trim(), '```', '');
        count++;
    }
    writeFileSync(p.importText, lines.join('\n'), 'utf-8');
    return count;
}

function requireGeneratedUnitDefinition(unitId) {
    if (typeof unitId !== 'string' || !/^[a-z][a-z0-9-]*$/.test(unitId)) {
        throw new Error('編集対象が不正です。');
    }
    const unit = pipelineConfig().generate.units.find((entry) => entry.id === unitId);
    if (!unit) throw new Error(`編集対象が見つかりません: ${unitId}`);
    return unit;
}

/** ⑤-2で生成済みの1ファイルをJSON編集ダイアログへ返す。 */
export function readGeneratedUnitJson(projectId, unitId) {
    const unit = requireGeneratedUnitDefinition(unitId);
    const p = projectPaths(projectId);
    const fileName = `${unit.id}.json`;
    const abs = path.join(p.generatedDir, fileName);
    if (!existsSync(abs)) throw new Error(`${fileName} はまだ生成されていません。`);
    return { unitId: unit.id, label: unit.label, fileName, content: readFileSync(abs, 'utf-8') };
}

/** 手動編集した⑤-2 JSONを検証・保存し、取り込み用テキストも同時に更新する。 */
export function saveGeneratedUnitJson(projectId, unitId, text) {
    const unit = requireGeneratedUnitDefinition(unitId);
    if (typeof text !== 'string' || text.trim() === '') throw new Error('JSONが空です。');
    let json;
    try {
        json = JSON.parse(text);
    } catch (e) {
        throw new Error(`JSON構文エラー: ${e.message}`);
    }
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
        throw new Error('JSONの最上位はオブジェクトにしてください。');
    }

    const meta = readProjectMeta(projectId);
    const analysis = requireAnalysis(projectId);
    if (!meta.gameId) throw new Error('先に「② ベースデータ生成」を実行してください。');
    const startMapId = analysis.game.startMapId;
    const target = unit.target.replaceAll('{startMapId}', startMapId);
    const gameDir = requireGameDir(meta.gameId);
    const design = readNormalizedWorldDesign(projectId, analysis);
    if (design) {
        const castModels = castModelsFromPromptIndex(readJsonIfExists(projectPaths(projectId).imagePromptsIndex));
        json = applyWorldDesignToUnit(unit.id, json, {
            startMapId,
            design,
            existingTarget: readJsonIfExists(path.join(gameDir, target)),
            gameDir,
            analysis,
            castModels,
        });
    }
    const schema = validateSingleJson(meta.gameId, target, json);
    if (!schema.ok && !schema.skipped) {
        throw new Error(`入力内容に問題があります: ${schema.errors.slice(0, 8).join(' / ')}`);
    }

    const p = projectPaths(projectId);
    mkdirSync(p.generatedDir, { recursive: true });
    writeJson(path.join(p.generatedDir, `${unit.id}.json`), json);
    const status = readJsonIfExists(p.generatedStatus) ?? { formatVersion: '1.0', units: {} };
    status.units ??= {};
    status.units[unit.id] = {
        ...(status.units[unit.id] ?? {}),
        ok: true,
        target,
        at: new Date().toISOString(),
        schema: schema.skipped ? 'skipped' : 'ok',
        edited: true,
    };
    status.importEntries = rebuildImportText(projectId, startMapId);
    status.generatedAt = new Date().toISOString();
    writeJson(p.generatedStatus, status);

    const llmUnits = pipelineConfig().generate.units;
    const okUnits = llmUnits.filter((entry) => status.units[entry.id]?.ok).length;
    const ngUnits = llmUnits.length - okUnits;
    recordStep(projectId, 'generateJson', { ok: ngUnits === 0, okUnits, ngUnits, editedUnit: unit.id });
    auditLog('story-generated-json-edit', { projectId, gameId: meta.gameId, unitId: unit.id, schema: schema.skipped ? 'skipped' : 'ok' });
    return {
        unitId: unit.id,
        label: unit.label,
        fileName: `${unit.id}.json`,
        content: readFileSync(path.join(p.generatedDir, `${unit.id}.json`), 'utf-8'),
        schema: schema.skipped ? 'skipped' : 'ok',
        importEntries: status.importEntries,
    };
}

/**
 * ⑤-2 本体: 選択ユニットを LLM で順次生成し、コード生成ユニットと i18n パッチも更新する。
 * @param {object} opts { units?: 生成する LLM ユニット id 配列（省略時は全部）, requireComplete?: 一括実行用の全必須ユニット完成ゲート, onLog }
 */
export async function runGenerateJson(projectId, {
    units = null, requireComplete = false, onLog = () => {}, signal = null, checkpoint = null,
} = {}) {
    const meta = readProjectMeta(projectId);
    const analysis = requireAnalysis(projectId);
    if (!meta.gameId) throw new Error('ベースデータが未生成です。先に「② ベースデータ生成」を実行してください。');
    const gameId = meta.gameId;
    const gameDir = requireGameDir(gameId);
    const design = readNormalizedWorldDesign(projectId, analysis);
    if (!design) throw new Error('ワールド設計がありません。先に「④ 初期ワールド設計」を実行してください。');
    const requestMd = readRequestText(projectId) ?? buildRequestText(projectId).requestText;
    const startMapId = analysis.game.startMapId;
    const p = projectPaths(projectId);
    mkdirSync(p.generatedDir, { recursive: true });
    const status = readJsonIfExists(p.generatedStatus) ?? { formatVersion: '1.0', units: {} };
    status.units ??= {};
    const promptIndex = readJsonIfExists(p.imagePromptsIndex);
    const visualProfiles = new Map(asArray(promptIndex?.prompts)
        .filter((entry) => entry?.castId && entry?.visualProfile)
        .map((entry) => [entry.castId, entry.visualProfile]));
    const motionProfiles = new Map(asArray(promptIndex?.prompts)
        .filter((entry) => entry?.castId && entry?.motionProfile)
        .map((entry) => [entry.castId, entry.motionProfile]));
    // ③の生成状況（GLB の有無・保存先・体格）: NPC/敵の専用モデル差し替えと敵タイプ登録に使う
    const castModels = castModelsFromPromptIndex(promptIndex);
    const catalogs = buildCatalogs(gameDir);
    const allUnits = pipelineConfig().generate.units;
    const selected = allUnits.filter((u) => !units || units.includes(u.id));
    if (selected.length === 0 && units) throw new Error(`指定ユニットが見つかりません: ${units.join(', ')}（有効: ${allUnits.map((u) => u.id).join(', ')}）`);

    /**
     * ⑤-2の「今回要求した処理の成否」と「全必須ユニットの完成状態」を分けて確定する。
     * 個別選択では成功した選択分を保存できる一方、一括実行(requireComplete=true)は
     * 1件でも欠けたら必ず failed となり、⑤-3へ誤って進ませない。
     */
    const finalizeStatus = async ({ importCount = status.importEntries ?? 0 } = {}) => {
        const missingRequired = missingGeneratedLlmUnitIds(status);
        const requiredOkCount = allUnits.length - missingRequired.length;
        const requiredIds = new Set(allUnits.map((unit) => unit.id));
        const auxiliaryFailures = Object.entries(status.units)
            .filter(([unitId, entry]) => !requiredIds.has(unitId) && !entry?.ok);
        const selectedFailures = selected.filter((unit) => !status.units[unit.id]?.ok);
        const ngCount = missingRequired.length + auxiliaryFailures.length;
        const complete = ngCount === 0;
        const operationOk = selectedFailures.length === 0
            && auxiliaryFailures.length === 0
            && (!requireComplete || complete);
        const failureDetails = [
            ...selectedFailures.map((unit) => `${unit.id}: ${status.units[unit.id]?.error ?? '未生成'}`),
            ...auxiliaryFailures.map(([unitId, entry]) => `${unitId}: ${entry?.error ?? '生成失敗'}`),
        ];
        if (requireComplete && missingRequired.length > 0 && selectedFailures.length === 0) {
            failureDetails.push(`未生成の必須ユニット: ${missingRequired.join(', ')}`);
        }

        status.ok = operationOk;
        status.complete = complete;
        status.okUnits = requiredOkCount;
        status.ngUnits = ngCount;
        status.importEntries = importCount;
        status.generatedAt = new Date().toISOString();
        if (missingRequired.length > 0) status.missingUnits = missingRequired;
        else delete status.missingUnits;
        if (!operationOk) {
            status.error = `⑥-2 JSON生成が未完了です（必須 ${requiredOkCount}/${allUnits.length}）。${failureDetails.join(' / ')}。成功済みユニットは保存済みのため、再実行すると未生成分だけ続行できます。`;
        } else {
            delete status.error;
        }

        writeJson(p.generatedStatus, status);
        recordStep(projectId, 'generateJson', {
            ok: complete,
            okUnits: requiredOkCount,
            ngUnits: ngCount,
            ...(missingRequired.length > 0 ? { missingUnits: missingRequired } : {}),
        });
        auditLog('story-generate-json', {
            projectId, gameId, ok: requiredOkCount, ng: ngCount,
            operationOk, requireComplete, missingUnits: missingRequired,
        });
        onLog(complete
            ? `✔ JSON 生成完了: 必須 ${requiredOkCount}/${allUnits.length} / 失敗・未生成 0（import.txt に ${importCount}ファイル）`
            : `✖ JSON 生成未完了: 必須 ${requiredOkCount}/${allUnits.length} / 失敗・未生成 ${ngCount}（${missingRequired.join(', ') || '補助生成'}）。成功分を保存し、次回は未生成分だけ再開します`);
        // 失敗ユニットが最後でも、ブラウザIndexedDBへ失敗状態と直前までの成功結果を確実に残す。
        await checkpoint?.();
        return status;
    };

    for (const unit of selected) {
        signal?.throwIfAborted(); // 中断チェック（ユニット境界。生成済みユニットは status に保存済み＝再実行で続きから）
        const target = unit.target.replaceAll('{startMapId}', startMapId);
        onLog(`── ${unit.id}: ${unit.label} → ${target}`);
        const unitLines = [];
        const unitLog = (line) => {
            unitLines.push(String(line));
            onLog(line);
        };
        try {
            const generatedMeta = unit.id === 'events' ? readJsonIfExists(path.join(p.generatedDir, 'meta.json')) : null;
            const result = await generateUnit({
                unit, target, requestMd, analysis, design, catalogs, gameId, gameDir, projectId, startMapId,
                generatedMeta, visualProfiles, motionProfiles, castModels, onLog: unitLog,
            });
            writeJson(path.join(p.generatedDir, `${unit.id}.json`), result.json);
            const costSummary = summarizeCostLines(unitLines);
            status.units[unit.id] = {
                ok: true, target, at: new Date().toISOString(), model: result.model,
                usage: result.usage, schema: result.schema.skipped ? 'skipped' : 'ok',
                ...(costSummary ? { costSummary } : {}),
            };
            onLog(`  ✔ 生成完了（スキーマ: ${result.schema.skipped ? 'スキップ' : 'OK'}）`);
        } catch (e) {
            const costSummary = summarizeCostLines(unitLines);
            status.units[unit.id] = {
                ok: false, target, at: new Date().toISOString(), error: e.message,
                ...(costSummary ? { costSummary } : {}),
            };
            onLog(`  ✖ 失敗: ${e.message}`);
            writeJson(p.generatedStatus, status);
            // キー未設定・無効・残高切れは残りユニットも全て失敗するため即中断する
            if (['token_missing', 'openrouter_auth', 'openrouter_credits'].includes(e.code)) throw e;
            continue;
        }
        writeJson(p.generatedStatus, status);
        // ブラウザ完結版向けのユニット境界チェックポイント（IndexedDB への中間 flush）。
        // タブクラッシュ時に支払い済みの生成結果を失わないため、書き終えた直後に永続化する
        await checkpoint?.();
    }

    // 要求されたLLMユニットが1件でも失敗した時点で、補助コード生成やi18n翻訳を始めない。
    // 部分データへの余分な翻訳課金を避け、成功済みユニットをチェックポイント保存して終了する。
    if (selected.some((unit) => !status.units[unit.id]?.ok)) {
        return finalizeStatus();
    }

    // コード生成ユニット（LLM 不使用・常に最新化）
    writeJson(path.join(p.generatedDir, 'game-info.json'), buildGameInfoJson(gameDir, analysis));
    status.units['game-info'] = { ok: true, target: 'maps/_shared/game-info.json', at: new Date().toISOString(), kind: 'code' };
    onLog('── game-info: novelInfo をコード生成（weapons 等は既存維持）');

    // 敵タイプ登録: 全敵castをランク能力倍率つき専用タイプへし、③の生成GLBも反映（LLM 不使用）
    const castEnemyTypes = buildCastEnemyTypesJson({
        master: readJsonIfExists(path.join(gameDir, 'maps/_shared/enemies.json')),
        analysis,
        castModels,
        generatedEnemies: readJsonIfExists(path.join(p.generatedDir, 'enemies.json')),
    });
    writeJson(path.join(p.generatedDir, 'enemy-types.json'), castEnemyTypes.document);
    status.units['enemy-types'] = {
        ok: true, target: 'maps/_shared/enemies.json', at: new Date().toISOString(), kind: 'code', added: castEnemyTypes.added,
    };
    onLog(`── enemy-types: 物語敵 ${castEnemyTypes.added}種をランク能力倍率つきで _shared/enemies.json へ登録`);

    // 全作品必須品と物語固有アイテムを開始マップのショップへ配置する
    //（LLM の商品参照を信用せずコードで整合させる）。
    const storyShop = buildStoryShopJson({
        items: readJsonIfExists(path.join(p.generatedDir, 'items.json')),
        startMapId,
    });
    writeJson(path.join(p.generatedDir, 'story-shop.json'), storyShop);
    status.units['story-shop'] = {
        ok: true,
        target: `maps/${startMapId}/shops.json`,
        at: new Date().toISOString(),
        kind: 'code',
        entries: storyShop.shopProfiles[0].exclusiveEntries.length,
    };
    const storyShopGeneratedCount = storyShop.shopProfiles[0].exclusiveEntries
        .filter((entry) => entry.itemId !== TREASURE_CHEST_COMPASS_ITEM_ID).length;
    onLog(`── story-shop: 必須品 1件 + 物語固有アイテム ${storyShopGeneratedCount}件を販売対象へ登録`);

    // i18n パッチ（生成済みユニットの生文字列から作る）
    const i18nLines = [];
    const i18nLog = (line) => {
        i18nLines.push(String(line));
        onLog(line);
    };
    try {
        const i18n = await buildI18nPatches({ projectId, analysis, generatedDir: p.generatedDir, onLog: i18nLog });
        const costSummary = summarizeCostLines(i18nLines);
        status.units['i18n'] = {
            ok: true, at: new Date().toISOString(), ...i18n,
            ...(costSummary ? { costSummary } : {}),
        };
    } catch (e) {
        const costSummary = summarizeCostLines(i18nLines);
        status.units['i18n'] = {
            ok: false, at: new Date().toISOString(), error: e.message,
            ...(costSummary ? { costSummary } : {}),
        };
        onLog(`  ✖ i18n パッチ生成に失敗: ${e.message}`);
    }

    const importCount = rebuildImportText(projectId, startMapId);
    return finalizeStatus({ importCount });
}

// ── ⑤-3 取り込み ─────────────────────────

/** 取り込みのドライラン（F2 のプラン + i18n パッチの概要）。 */
export function buildImportPlan(projectId) {
    const meta = readProjectMeta(projectId);
    if (!meta.gameId) throw new Error('ベースデータが未生成です（② ベースデータ生成を先に実行）。');
    const p = projectPaths(projectId);
    requireCompleteGeneratedLlmUnits(projectId);
    if (!existsSync(p.importText)) throw new Error('生成結果（generated/import.txt）がありません。先に「⑥-2 JSON 生成」を実行してください。');
    const text = readFileSync(p.importText, 'utf-8');
    const { plan } = importFromText(meta.gameId, text, { dryRun: true });
    const i18n = {
        ja: readJsonIfExists(path.join(p.generatedDir, 'i18n-patch/ja.patch.json')) ? true : false,
        en: readJsonIfExists(path.join(p.generatedDir, 'i18n-patch/en.patch.json')) ? true : false,
    };
    return { gameId: meta.gameId, plan: plan.map(({ absPath, json, jsonText, ...rest }) => rest), i18nPatches: i18n };
}

/** 取り込み本体（F2 で配置 + i18n パッチをキー単位マージ）。上書き前提（プレースホルダ差し替え）。 */
export function runImport(projectId, { onLog = () => {} } = {}) {
    const meta = readProjectMeta(projectId);
    if (!meta.gameId) throw new Error('ベースデータが未生成です（② ベースデータ生成を先に実行）。');
    const gameId = meta.gameId;
    const gameDir = requireGameDir(gameId);
    const p = projectPaths(projectId);
    requireCompleteGeneratedLlmUnits(projectId);
    if (!existsSync(p.importText)) throw new Error('生成結果（generated/import.txt）がありません。先に「⑥-2 JSON 生成」を実行してください。');
    const text = readFileSync(p.importText, 'utf-8');
    // applyImport は「エラーが 1 件でもあれば全件未適用」を保証するため、1 回の呼び出しで
    // 検証と適用が済む（別途ドライランを挟むと同じテキストを二重パースするだけで保証は増えない）。
    // 適用成功のステータスは created / overwritten / merged（merged はロケールのキー単位マージ）。
    const { plan, results } = importFromText(gameId, text, { confirmOverwrite: true });
    if (plan.length === 0) {
        throw new Error('取り込める JSON がありません。⑥-2 JSON 生成を再実行してください。');
    }
    const appliedStatus = new Set(['created', 'overwritten', 'merged']);
    for (const r of results) onLog(`  ${appliedStatus.has(r.status) ? '✔' : '✖'} ${r.relPath} ${r.status}${r.reasons.length ? `（${r.reasons.join(' / ')}）` : ''}`);
    const blocked = results.some((r) => !appliedStatus.has(r.status));
    if (blocked) {
        const rejectedCount = results.filter((r) => r.status === 'rejected').length;
        recordStep(projectId, 'import', { ok: false, written: 0, rejected: rejectedCount, i18nMerged: {} });
        throw new Error(`取り込み前検証で ${rejectedCount}ファイルに問題が見つかりました。i18n を含め、どのファイルも変更していません。`);
    }

    const reapplied = reapplyWorldDesignArtifacts(projectId, gameId);
    if (reapplied.updated > 0) {
        onLog(`  ✔ 現行ワールド設計を ${reapplied.units.join(' / ')} へ再反映`);
    }
    for (const text of reapplied.i18nIdentityTexts ?? []) {
        onLog(`  ⚠ 対訳が未生成のため原文のまま英語辞書へ登録: 「${text}」`
            + '（⑥-2 JSON生成を再実行すると英訳で上書きされます。en.json exact の手修正でも可）');
    }

    const i18nMerged = {};
    for (const lang of ['ja', 'en']) {
        const patch = readJsonIfExists(path.join(p.generatedDir, `i18n-patch/${lang}.patch.json`));
        if (!patch) continue;
        const localePath = path.join(gameDir, `maps/_shared/i18n/locales/${lang}.json`);
        const locale = readJson(localePath);
        let merged = 0;
        if (patch.messages) merged += deepMergeInto((locale.messages ??= {}), patch.messages);
        if (patch.exact) merged += deepMergeInto((locale.exact ??= {}), patch.exact);
        writeJson(localePath, locale);
        i18nMerged[lang] = merged;
        onLog(`  ✔ locales/${lang}.json へ ${merged} キーをマージ`);
    }
    const written = results.filter((r) => appliedStatus.has(r.status)).length;
    const rejected = results.filter((r) => r.status === 'rejected').length;
    recordStep(projectId, 'import', { ok: rejected === 0, written, rejected, i18nMerged });
    auditLog('story-import', { projectId, gameId, written, rejected });
    onLog(`✔ 取り込み完了: 配置 ${written} / 拒否 ${rejected}。この後 generate:treasures と validate を実行してください`);
    return { plan: plan.map(({ absPath, json, jsonText, ...rest }) => rest), results, i18nMerged };
}
